import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CloseIcon from "@mui/icons-material/Close";
import LibraryAddTwoToneIcon from "@mui/icons-material/LibraryAddTwoTone";
import DriveFileRenameOutlineTwoToneIcon from "@mui/icons-material/DriveFileRenameOutlineTwoTone";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PeopleTwoToneIcon from "@mui/icons-material/PeopleTwoTone";
import RecordVoiceOverTwoToneIcon from "@mui/icons-material/RecordVoiceOverTwoTone";
import ExtensionTwoToneIcon from "@mui/icons-material/ExtensionTwoTone";
import PersonAddTwoToneIcon from "@mui/icons-material/PersonAddTwoTone";
import EventTwoToneIcon from "@mui/icons-material/EventTwoTone";
import LabelImportantTwoToneIcon from "@mui/icons-material/LabelImportantTwoTone";
import NoteAltTwoToneIcon from "@mui/icons-material/NoteAltTwoTone";
import StyleTwoToneIcon from "@mui/icons-material/StyleTwoTone";
import NewLabelTwoToneIcon from "@mui/icons-material/NewLabelTwoTone";
import PlaylistAddCheckCircleTwoToneIcon from "@mui/icons-material/PlaylistAddCheckCircleTwoTone";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import AccessAlarmIcon from "@mui/icons-material/AccessAlarm";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import SettingsTwoToneIcon from "@mui/icons-material/SettingsTwoTone";
import SettingsIcon from "@mui/icons-material/Settings";
import RefreshIcon from "@mui/icons-material/Refresh";
import ListAltIcon from "@mui/icons-material/ListAlt";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import KebabDiningOutlinedIcon from "@mui/icons-material/KebabDiningOutlined";
import FlagTwoToneIcon from "@mui/icons-material/FlagTwoTone";
import { useState } from "react";
import type {
  SandboxAutoReminderForm,
  SandboxCustomColumn,
  SandboxList,
  SandboxPriority,
  SandboxReminder,
  SandboxStatus,
  SandboxStatusDef,
  SandboxTag,
  SandboxTask,
  SandboxTaskDraft,
  SettingsTab,
} from "./types.ts";
import {
  SANDBOX_CUSTOM_COLUMNS,
  SANDBOX_DEFAULT_STATUSES,
  SANDBOX_MEMBERS,
  SANDBOX_TAGS,
} from "./types.ts";

const PRIORITIES: SandboxPriority[] = ["なし", "高", "中", "低"];

/** 標準ステータスの色（フォールバック用） */
const STATUS_COLOR: Record<string, string> = {
  未対応: "#8A94A6",
  対応中: "#3989FA",
  完了: "#40C063",
  非表示: "#8A94A6",
};

/** ステータス名から表示色を解決する（ルーム追加分も考慮） */
const resolveStatusColor = (
  name: string,
  statuses: SandboxStatusDef[],
): string =>
  statuses.find((s) => s.name === name)?.color ??
  STATUS_COLOR[name] ??
  "#8A94A6";

const PRIORITY_BADGE: Record<SandboxPriority, { bg: string; label: string }> = {
  高: { bg: "#F85B52", label: "高" },
  中: { bg: "#FCA733", label: "中" },
  低: { bg: "#3989FA", label: "低" },
  なし: { bg: "", label: "" },
};

/** ISO 文字列を YYYY/MM/DD 形式に整形 */
const formatYmd = (iso?: string): string => {
  if (!iso) return "ー";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "ー";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}/${m}/${day}`;
};

export interface SandboxScreenProps {
  accent: string;
  isDark: boolean;
  lists: SandboxList[];
  activeListId: string | null;
  selectedTaskId: string | null;
  showCompleted: boolean;
  statusMenuOpen: boolean;
  listDialog: { open: boolean; name: string };
  taskDialog: SandboxTaskDraft;
  /** 選択可能なステータス（未指定なら標準4種）。ルーム設定で追加されうる */
  statuses?: SandboxStatusDef[];
  /** 選択可能なタグ（未指定なら標準ダミー） */
  tags?: SandboxTag[];
  /** カスタム項目定義（未指定なら標準ダミー） */
  customColumns?: SandboxCustomColumn[];
  /** 全体設定ダイアログの状態。未指定なら設定ボタン・ダイアログは非表示 */
  settings?: { open: boolean; tab: SettingsTab };
  /** 「表示するカスタム項目」ダイアログの状態 */
  customDisplay?: { open: boolean };
  /** タスクカードに表示中のカスタム項目ID（表示順） */
  visibleCustomColumnIds?: number[];
  // ===== 繰り返し作成チュートリアル用（任意） =====
  /** 自動作成ルール設定ダイアログの開閉。showForm=true で頻度設定フォームを表示 */
  recurrenceDialog?: { open: boolean; showForm: boolean };
  /** 作成済みの繰り返しルール（未作成なら null） */
  recurrenceRule?: {
    title: string;
    summary: string;
    priority: SandboxPriority;
    memo: string;
  } | null;
  /** 繰り返し作成一覧ダイアログの開閉 */
  recurrenceListOpen?: boolean;
  /** 自動作成設定の更新プロンプト（作成元/自動作成の別を含む） */
  updatePrompt?: { open: boolean; isOriginal: boolean };
  // ===== リマインダーチュートリアル用（任意） =====
  /** リマインダーダイアログの開閉 */
  reminderDialog?: { open: boolean };
  /** 自動リマインダー設定フォームの状態（設定ダイアログの「自動リマインダー」タブで使用） */
  autoReminderForm?: SandboxAutoReminderForm;
  /** 自動リマインダー設定が保存済みか */
  autoReminderSaved?: boolean;
  on: {
    projectAdd: () => void;
    listNameChange: (v: string) => void;
    listCreate: () => void;
    listCancel: () => void;
    fab: () => void;
    taskTitleChange: (v: string) => void;
    taskPriorityChange: (p: SandboxPriority) => void;
    taskDateChange: (field: "startDate" | "endDate", v: string) => void;
    taskToggleAssignee: (name: string) => void;
    taskToggleRequester: (name: string) => void;
    taskToggleTag: (name: string) => void;
    taskCustomChange: (columnId: number, v: string) => void;
    taskMemoChange: (v: string) => void;
    taskCreate: () => void;
    taskCancel: () => void;
    selectTask: (id: string) => void;
    detailPriorityChange: (p: SandboxPriority) => void;
    detailMemoChange: (v: string) => void;
    toggleStatusMenu: () => void;
    changeStatus: (s: SandboxStatus) => void;
    toggleComplete: (id: string) => void;
    toggleShowCompleted: () => void;
    // ===== ルームごとの設定チュートリアル用（任意） =====
    openSettings?: () => void;
    closeSettings?: () => void;
    setSettingsTab?: (tab: SettingsTab) => void;
    addStatus?: (name: string, color: string) => void;
    addTag?: (name: string, color: string) => void;
    addCustomColumn?: (
      name: string,
      type: "text" | "select",
      options: string[],
    ) => void;
    /** 詳細パネルでステータスメニューを開いたとき */
    detailOpenStatusMenu?: () => void;
    /** 詳細パネルでステータスを変更 */
    detailChangeStatus?: (s: SandboxStatus) => void;
    /** 詳細パネルでタグをトグル */
    detailToggleTag?: (name: string) => void;
    /** 詳細パネルでカスタム項目の値を変更 */
    detailCustomChange?: (columnId: number, v: string) => void;
    /** 「表示項目の変更」ダイアログを開く */
    openCustomDisplay?: () => void;
    /** 表示項目ダイアログを閉じる */
    closeCustomDisplay?: () => void;
    /** 表示するカスタム項目（表示順）を保存 */
    saveCustomDisplay?: (visibleIds: number[]) => void;
    // ===== 繰り返し作成チュートリアル用（任意） =====
    /** 詳細パネルの「繰り返し」ボタンを押す（自動作成ルール設定ダイアログを開く） */
    openRecurrenceDialog?: () => void;
    /** 「ルールを追加」を押して頻度設定フォームを表示する */
    showRecurrenceForm?: () => void;
    /** 自動作成ルール設定ダイアログを閉じる */
    closeRecurrenceDialog?: () => void;
    /** 頻度を指定して繰り返しルールを保存する（引数は頻度の要約文字列） */
    saveRecurrence?: (frequency: string) => void;
    /** 繰り返し作成一覧を開く */
    openRecurrenceList?: () => void;
    /** 繰り返し作成一覧を閉じる */
    closeRecurrenceList?: () => void;
    /** 繰り返し作成一覧の詳細で重要度を変更する */
    editRulePriority?: (p: SandboxPriority) => void;
    /** 繰り返し作成一覧の詳細でメモを変更する */
    editRuleMemo?: (v: string) => void;
    /** 更新プロンプトで「更新する」 */
    confirmUpdatePrompt?: () => void;
    /** 更新プロンプトで「しない」／閉じる */
    dismissUpdatePrompt?: () => void;
    // ===== リマインダーチュートリアル用（任意） =====
    /** 詳細パネルの「リマインダー」ボタンを押す（リマインダーダイアログを開く） */
    openReminderDialog?: () => void;
    /** リマインダーダイアログを閉じる */
    closeReminderDialog?: () => void;
    /** 日時を指定してリマインダーを追加する（引数は表示用の日時文字列） */
    addReminder?: (label: string) => void;
    /** リマインダーを削除する */
    deleteReminder?: (id: string) => void;
    /** 自動リマインダーのON/OFFを切り替える */
    toggleAutoReminder?: () => void;
    /** 自動リマインダーフォームの値を更新する */
    updateAutoReminderForm?: (
      updates: Partial<SandboxAutoReminderForm>,
    ) => void;
    /** 自動リマインダー設定を保存する */
    saveAutoReminder?: () => void;
  };
}

/**
 * 実画面（DesktopView）を簡略再現した練習用の静的画面。
 * すべて props 経由の制御コンポーネントで、内部に永続状態を持たない。
 *
 * 実画面の見た目（サイドバー / タスク行 / 詳細パネル / 各ダイアログ）に寄せている。
 * ただし操作の主役ではない一部の要素（フィルタートグル・通知/スター・詳細パネルの
 * フッターボタン等）は雰囲気を出すための装飾で、クリックしても何も起きない。
 */
export const SandboxScreen = ({
  accent,
  isDark,
  lists,
  activeListId,
  selectedTaskId,
  showCompleted,
  statusMenuOpen,
  listDialog,
  taskDialog,
  statuses = SANDBOX_DEFAULT_STATUSES,
  tags = SANDBOX_TAGS,
  customColumns = SANDBOX_CUSTOM_COLUMNS,
  settings,
  customDisplay,
  visibleCustomColumnIds = [],
  recurrenceDialog,
  recurrenceRule,
  recurrenceListOpen,
  updatePrompt,
  reminderDialog,
  autoReminderForm,
  autoReminderSaved,
  on,
}: SandboxScreenProps) => {
  const surface = isDark ? "#1e1e1e" : "#ffffff";
  const pageBg = isDark ? "#121212" : "#f5f5f5";
  const text = isDark ? "#e0e0e0" : "#212121";
  const subText = isDark ? "#9e9e9e" : "#757575";
  const border = isDark ? "#333333" : "#e0e0e0";
  const rowBg = isDark ? "#1e1e1e" : "#ffffff";
  const inputBg = isDark ? "#2a2a2a" : "#ffffff";
  const chipBg = isDark ? "#2a2a2a" : "#f3f4f6"; // ステータスピル等の薄いグレー背景

  const activeList = lists.find((l) => l.id === activeListId) ?? null;
  const selectedTask =
    activeList?.tasks.find((t) => t.id === selectedTaskId) ?? null;

  const visibleTasks =
    activeList?.tasks.filter((t) => showCompleted || !t.completed) ?? [];

  // 実画面のアクティブプロジェクト / FAB と同じ立体的なアクセントスタイル
  const activeStyle: React.CSSProperties = {
    backgroundColor: accent,
    backgroundImage:
      "linear-gradient(to bottom, rgba(255,255,255,0.15), rgba(255,255,255,0))",
    boxShadow:
      "inset 0 1px 2px rgba(255,255,255,0.25), 0 3px 5px rgba(0,0,0,0.15)",
  };

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        backgroundColor: pageBg,
        color: text,
        overflow: "hidden",
      }}
    >
      {/* ===== 左サイドバー ===== */}
      <aside
        style={{
          width: 320,
          flexShrink: 0,
          backgroundColor: surface,
          borderRight: `2px solid ${border}`,
          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: 16,
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          {/* プロジェクトセクション */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 6,
              }}
            >
              <span
                style={{ fontSize: "0.8rem", fontWeight: 700, color: subText }}
              >
                プロジェクト
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                <button
                  type="button"
                  data-tut="project-add"
                  onClick={on.projectAdd}
                  aria-label="プロジェクトを新規作成"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    color: subText,
                    padding: 6,
                    borderRadius: 6,
                  }}
                >
                  <LibraryAddTwoToneIcon style={{ fontSize: 18 }} />
                </button>
                <button
                  type="button"
                  aria-label="プロジェクトを編集"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    color: subText,
                    padding: 6,
                    borderRadius: 6,
                  }}
                >
                  <DriveFileRenameOutlineTwoToneIcon style={{ fontSize: 18 }} />
                </button>
                <span style={{ display: "flex", color: subText, padding: 6 }}>
                  <ExpandMoreIcon style={{ fontSize: 18 }} />
                </span>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {lists.length === 0 ? (
                <p
                  style={{
                    fontSize: "0.75rem",
                    color: subText,
                    fontStyle: "italic",
                    padding: "4px 8px",
                  }}
                >
                  まだプロジェクトがありません
                </p>
              ) : (
                lists.map((l) => {
                  const isActive = l.id === activeListId;
                  return (
                    <div
                      key={l.id}
                      data-tut={isActive ? "active-project" : undefined}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "5px 8px",
                        borderRadius: 8,
                        fontSize: "0.85rem",
                        fontWeight: 600,
                        color: isActive ? "#fff" : text,
                        ...(isActive ? activeStyle : {}),
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          minWidth: 0,
                          flex: 1,
                        }}
                      >
                        <PlaylistAddCheckCircleTwoToneIcon
                          style={{
                            fontSize: 18,
                            marginRight: 8,
                            flexShrink: 0,
                            color: isActive ? "#fff" : subText,
                          }}
                        />
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {l.name}
                        </span>
                      </div>
                      <MoreVertIcon
                        style={{
                          fontSize: 16,
                          flexShrink: 0,
                          color: isActive ? "#fff" : subText,
                          opacity: 0.45,
                        }}
                      />
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* ソートセクション（装飾） */}
          <div style={{ borderTop: `1px solid ${border}`, paddingTop: 12 }}>
            <SidebarSectionTitle subText={subText}>ソート</SidebarSectionTitle>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div
                style={{
                  flex: 1,
                  padding: "6px 8px",
                  fontSize: "0.8rem",
                  border: `1px solid ${border}`,
                  borderRadius: 6,
                  backgroundColor: inputBg,
                  color: text,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                作成日順
                <ExpandMoreIcon style={{ fontSize: 16, color: subText }} />
              </div>
              <span
                style={{
                  display: "flex",
                  padding: 6,
                  border: `1px solid ${border}`,
                  borderRadius: 6,
                  color: subText,
                }}
              >
                <SwapVertIcon style={{ fontSize: 16 }} />
              </span>
            </div>
          </div>

          {/* フィルターセクション */}
          <div style={{ borderTop: `1px solid ${border}`, paddingTop: 12 }}>
            <SidebarSectionTitle subText={subText}>
              フィルター
            </SidebarSectionTitle>

            {/* 検索ボックス（装飾） */}
            <div style={{ position: "relative", marginBottom: 10 }}>
              <SearchIcon
                style={{
                  position: "absolute",
                  left: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: 16,
                  color: subText,
                }}
              />
              <input
                type="text"
                readOnly
                placeholder="メインタスク名で絞り込み"
                style={{
                  width: "100%",
                  padding: "6px 8px 6px 30px",
                  fontSize: "0.8rem",
                  border: `1px solid ${border}`,
                  borderRadius: 6,
                  outline: "none",
                  backgroundColor: inputBg,
                  color: text,
                }}
              />
            </div>

            <div
              style={{
                borderTop: `1px dashed ${border}`,
                paddingTop: 8,
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <FilterToggle label="自分が担当者" checked={false} labelColor={text} />
              <FilterToggle label="自分が依頼者" checked={false} labelColor={text} />
              <FilterToggle
                label="期限が今日を含むタスク"
                checked={false}
                labelColor={text}
              />
              <FilterToggle
                label="スター付きのタスク"
                checked={false}
                labelColor={text}
              />
              <div data-tut="show-completed" onClick={on.toggleShowCompleted}>
                <FilterToggle
                  label="完了タスクを表示"
                  checked={showCompleted}
                  labelColor={text}
                />
              </div>
              <FilterToggle
                label="非表示タスクを表示"
                checked={false}
                labelColor={text}
              />
            </div>
          </div>

          {/* その他セクション（装飾） */}
          <div style={{ borderTop: `1px solid ${border}`, paddingTop: 12 }}>
            <SidebarSectionTitle subText={subText}>その他</SidebarSectionTitle>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: 8,
                  borderRadius: 8,
                  fontSize: "0.85rem",
                  color: text,
                }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <AccessAlarmIcon style={{ fontSize: 18, color: subText }} />
                  リマインダー管理
                </span>
              </div>
              <div
                data-tut="sidebar-recurrence-list"
                onClick={on.openRecurrenceList}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: 8,
                  borderRadius: 8,
                  fontSize: "0.85rem",
                  color: text,
                  cursor: on.openRecurrenceList ? "pointer" : "default",
                }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <AutorenewIcon style={{ fontSize: 18, color: subText }} />
                  繰り返し作成一覧
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* 下部ボタン（装飾） */}
        <div style={{ padding: 16, borderTop: `1px solid ${border}` }}>
          <div
            style={{
              width: "100%",
              padding: "8px 16px",
              borderRadius: 8,
              fontSize: "0.85rem",
              fontWeight: 600,
              color: "#fff",
              textAlign: "center",
              border: "2px solid #fff",
              ...activeStyle,
            }}
          >
            詳細な絞り込み
          </div>
          <div
            style={{
              width: "100%",
              marginTop: 8,
              fontSize: "0.8rem",
              color: accent,
              textAlign: "center",
            }}
          >
            絞り込みをリセット
          </div>
        </div>
      </aside>

      {/* ===== 中央：タスク一覧 ===== */}
      <main
        style={{
          flex: 1,
          position: "relative",
          display: "flex",
          flexDirection: "column",
          padding: 24,
          // ツールバー（設定チュートリアル時のみ表示）と重ならないよう上部に余白
          paddingTop: on.openSettings ? 64 : 24,
          minWidth: 0,
        }}
      >
        {/* 浮遊ツールバー（実画面準拠: 更新｜ビュー切替｜全体設定）。設定チュートリアル時のみ */}
        {on.openSettings && (
          <div
            style={{
              position: "absolute",
              top: 16,
              left: 24,
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              gap: 2,
              padding: "2px 4px",
              backgroundColor: surface,
              border: `1px solid ${border}`,
              borderRadius: 8,
              boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
            }}
          >
            {/* 更新（装飾） */}
            <ToolbarIconButton subText={subText}>
              <RefreshIcon style={{ fontSize: 18 }} />
            </ToolbarIconButton>
            <ToolbarDivider border={border} />
            {/* ビュー切替（装飾。リストがアクティブ） */}
            <ToolbarIconButton subText={subText} active activeStyle={activeStyle}>
              <ListAltIcon style={{ fontSize: 18 }} />
            </ToolbarIconButton>
            <ToolbarIconButton subText={subText}>
              <TableChartOutlinedIcon style={{ fontSize: 18 }} />
            </ToolbarIconButton>
            <ToolbarIconButton subText={subText}>
              <AccountTreeOutlinedIcon style={{ fontSize: 18 }} />
            </ToolbarIconButton>
            <ToolbarIconButton subText={subText}>
              <CalendarMonthIcon style={{ fontSize: 18 }} />
            </ToolbarIconButton>
            <ToolbarIconButton subText={subText}>
              <KebabDiningOutlinedIcon style={{ fontSize: 18 }} />
            </ToolbarIconButton>
            <ToolbarDivider border={border} />
            {/* 全体設定 */}
            <button
              type="button"
              data-tut="settings-button"
              onClick={on.openSettings}
              aria-label="全体設定"
              title="全体設定"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 6,
                borderRadius: 6,
                color: subText,
              }}
            >
              <SettingsIcon style={{ fontSize: 20 }} />
            </button>
          </div>
        )}
        {activeList ? (
          <>
            <h2 style={{ fontSize: "1.25rem", fontWeight: 700, marginBottom: 12 }}>
              {activeList.name}
            </h2>
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              {visibleTasks.length === 0 ? (
                <p
                  style={{
                    color: subText,
                    fontSize: "0.875rem",
                    fontStyle: "italic",
                    marginTop: 24,
                  }}
                >
                  タスクがありません。右下の「＋」から作成しましょう。
                </p>
              ) : (
                visibleTasks.map((t) => {
                  // 「表示するカスタム項目」設定で選ばれた列を表示順に値と組み合わせる
                  const customItems = visibleCustomColumnIds
                    .map((id) => {
                      const col = customColumns.find((c) => c.id === id);
                      if (!col) return null;
                      return {
                        id,
                        name: col.name,
                        value: t.customValues[id] || "",
                      };
                    })
                    .filter(
                      (v): v is { id: number; name: string; value: string } =>
                        !!v,
                    );
                  return (
                    <TaskRow
                      key={t.id}
                      task={t}
                      isSelected={t.id === selectedTaskId}
                      statusMenuOpen={statusMenuOpen && t.id === selectedTaskId}
                      statuses={statuses}
                      accent={accent}
                      rowBg={rowBg}
                      chipBg={chipBg}
                      border={border}
                      text={text}
                      subText={subText}
                      customItems={customItems}
                      dataTut={t.sourceRecurring ? "auto-task-row" : "task-row"}
                      showRecurringIcon={!!t.sourceRecurring}
                      onSelect={() => on.selectTask(t.id)}
                      onToggleStatusMenu={on.toggleStatusMenu}
                      onChangeStatus={on.changeStatus}
                      onToggleComplete={() => on.toggleComplete(t.id)}
                    />
                  );
                })
              )}
            </div>

            {/* 表示するカスタム項目の設定（タスク一覧の下に表示・カスタム項目チュートリアル用） */}
            {on.openCustomDisplay && customColumns.length > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  // FAB（右下に浮遊）と重ならないよう右側に余白を確保する
                  paddingTop: 8,
                  paddingRight: 72,
                  marginTop: 4,
                  borderTop: `1px solid ${border}`,
                }}
              >
                <button
                  type="button"
                  data-tut="custom-display-button"
                  onClick={on.openCustomDisplay}
                  aria-label="表示するカスタム項目を設定"
                  title="表示するカスタム項目を設定"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    color: accent,
                    padding: "4px 10px",
                    borderRadius: 9999,
                  }}
                >
                  <ExtensionTwoToneIcon style={{ fontSize: 16 }} />
                  表示項目の変更
                </button>
              </div>
            )}

            {/* FAB */}
            <button
              type="button"
              data-tut="fab"
              onClick={on.fab}
              aria-label="タスクを新規作成"
              style={{
                position: "absolute",
                bottom: 24,
                right: 24,
                width: 56,
                height: 56,
                borderRadius: 9999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                border: "4px solid #fff",
                ...activeStyle,
              }}
            >
              <AddIcon />
            </button>
          </>
        ) : (
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: subText,
              fontSize: "0.9rem",
            }}
          >
            プロジェクトを作成してください
          </div>
        )}
      </main>

      {/* ===== 右：詳細パネル ===== */}
      <aside
        style={{
          width: 360,
          flexShrink: 0,
          backgroundColor: surface,
          borderLeft: `2px solid ${border}`,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {selectedTask ? (
          <DetailPanel
            task={selectedTask}
            accent={accent}
            isDark={isDark}
            text={text}
            subText={subText}
            border={border}
            inputBg={inputBg}
            chipBg={chipBg}
            rowBg={rowBg}
            statuses={statuses}
            tags={tags}
            customColumns={customColumns}
            onPriorityChange={on.detailPriorityChange}
            onMemoChange={on.detailMemoChange}
            onOpenStatusMenu={on.detailOpenStatusMenu}
            onChangeStatus={on.detailChangeStatus}
            onToggleTag={on.detailToggleTag}
            onCustomChange={on.detailCustomChange}
            onOpenRecurrence={on.openRecurrenceDialog}
            autoCreated={!!selectedTask.sourceRecurring}
            ruleSummary={recurrenceRule?.summary}
            onEditRule={on.openRecurrenceList}
            onOpenReminder={on.openReminderDialog}
          />
        ) : (
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: subText,
              fontSize: "0.875rem",
              padding: 16,
              textAlign: "center",
            }}
          >
            タスクを選択してください
          </div>
        )}
      </aside>

      {/* ===== プロジェクト作成ダイアログ ===== */}
      {listDialog.open && (
        <DialogShell surface={surface} border={border} maxWidth={448}>
          <div data-tut="list-dialog">
            <DialogGradientHeader
              accent={accent}
              title="プロジェクトの新規作成"
              onClose={on.listCancel}
            />
            <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 24 }}>
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    color: subText,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    marginBottom: 8,
                  }}
                >
                  プロジェクト名
                </label>
                <input
                  type="text"
                  autoFocus
                  placeholder="例: 日常業務"
                  value={listDialog.name}
                  onChange={(e) => on.listNameChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && listDialog.name.trim())
                      on.listCreate();
                  }}
                  style={{
                    width: "100%",
                    padding: 10,
                    border: `2px solid ${border}`,
                    borderRadius: 8,
                    outline: "none",
                    backgroundColor: inputBg,
                    color: text,
                  }}
                />
              </div>

              {/* テンプレート選択（装飾） */}
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    color: subText,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    marginBottom: 8,
                  }}
                >
                  テンプレートを選択 (任意)
                </label>
                <div
                  style={{
                    textAlign: "center",
                    padding: "24px 8px",
                    border: `2px dashed ${border}`,
                    borderRadius: 8,
                    color: subText,
                    fontSize: "0.75rem",
                  }}
                >
                  利用可能なテンプレートがありません
                </div>
              </div>
            </div>
            <DialogFooter surface={surface} border={border} grayBg={!isDark}>
              <DialogCancelButton
                onClick={on.listCancel}
                text={text}
                border={border}
              />
              <DialogConfirmButton
                onClick={on.listCreate}
                disabled={!listDialog.name.trim()}
                accent={accent}
                label="プロジェクトを作成"
              />
            </DialogFooter>
          </div>
        </DialogShell>
      )}

      {/* ===== タスク作成ダイアログ ===== */}
      {taskDialog.open && (
        <DialogShell surface={surface} border={border} maxWidth={560}>
          <div data-tut="task-dialog">
            <DialogPlainHeader
              title="新規タスク作成"
              text={text}
              subText={subText}
              border={border}
              onClose={on.taskCancel}
            />
            <div
              style={{
                padding: 20,
                maxHeight: "68vh",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 18,
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.8rem",
                    fontWeight: 700,
                    color: text,
                    marginBottom: 4,
                  }}
                >
                  タイトル <span style={{ color: "#F85B52" }}>*</span>
                </label>
                <input
                  type="text"
                  autoFocus
                  placeholder="例: 資料を作成する"
                  value={taskDialog.title}
                  onChange={(e) => on.taskTitleChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && taskDialog.title.trim())
                      on.taskCreate();
                  }}
                  style={{
                    width: "100%",
                    padding: 8,
                    border: `1px solid ${border}`,
                    borderRadius: 6,
                    outline: "none",
                    backgroundColor: inputBg,
                    color: text,
                  }}
                />
              </div>

              {/* 担当者・依頼者 */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 18,
                }}
              >
                <MemberPicker
                  label="担当者"
                  icon={
                    <PeopleTwoToneIcon
                      style={{ fontSize: 16, color: subText }}
                    />
                  }
                  selected={taskDialog.assignees}
                  onToggle={on.taskToggleAssignee}
                  accent={accent}
                  text={text}
                  subText={subText}
                  border={border}
                  inputBg={inputBg}
                  chipBg={chipBg}
                />
                <MemberPicker
                  label="依頼者"
                  icon={
                    <RecordVoiceOverTwoToneIcon
                      style={{ fontSize: 16, color: subText }}
                    />
                  }
                  selected={taskDialog.requesters}
                  onToggle={on.taskToggleRequester}
                  accent={accent}
                  text={text}
                  subText={subText}
                  border={border}
                  inputBg={inputBg}
                  chipBg={chipBg}
                />
              </div>

              {/* 期限・重要度 */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 18,
                }}
              >
                <DateField
                  startDate={taskDialog.startDate}
                  endDate={taskDialog.endDate}
                  onChange={on.taskDateChange}
                  accent={accent}
                  text={text}
                  subText={subText}
                  border={border}
                  inputBg={inputBg}
                  isDark={isDark}
                />
                <div>
                  <SectionLabel
                    icon={
                      <LabelImportantTwoToneIcon
                        style={{ fontSize: 16, color: subText }}
                      />
                    }
                    label="重要度"
                    subText={subText}
                  />
                  <div style={{ marginTop: 4 }}>
                    <PriorityButtons
                      value={taskDialog.priority}
                      text={text}
                      subText={subText}
                      isDark={isDark}
                      onChange={on.taskPriorityChange}
                    />
                  </div>
                </div>
              </div>

              {/* タグ */}
              <TagPicker
                tags={tags}
                selected={taskDialog.tags}
                onToggle={on.taskToggleTag}
                accent={accent}
                subText={subText}
                inputBg={inputBg}
              />

              {/* カスタム項目 */}
              <CustomColumnFields
                columns={customColumns}
                values={taskDialog.customValues}
                onChange={on.taskCustomChange}
                text={text}
                subText={subText}
                border={border}
                inputBg={inputBg}
              />

              {/* メモ */}
              <div>
                <SectionLabel
                  icon={
                    <NoteAltTwoToneIcon
                      style={{ fontSize: 16, color: subText }}
                    />
                  }
                  label="メモ"
                  subText={subText}
                />
                <textarea
                  value={taskDialog.memo}
                  onChange={(e) => on.taskMemoChange(e.target.value)}
                  placeholder="メモを入力..."
                  style={{
                    marginTop: 4,
                    width: "100%",
                    minHeight: 72,
                    padding: 8,
                    border: `2px solid ${border}`,
                    borderRadius: 8,
                    outline: "none",
                    resize: "vertical",
                    backgroundColor: inputBg,
                    color: text,
                    fontSize: "0.85rem",
                  }}
                />
              </div>
            </div>
            <DialogFooter surface={surface} border={border}>
              <DialogCancelButton
                onClick={on.taskCancel}
                text={text}
                border={border}
              />
              <DialogConfirmButton
                onClick={on.taskCreate}
                disabled={!taskDialog.title.trim()}
                accent={accent}
                label="作成"
              />
            </DialogFooter>
          </div>
        </DialogShell>
      )}

      {/* ===== 全体設定ダイアログ ===== */}
      {settings?.open && on.closeSettings && (
        <SettingsDialog
          tab={settings.tab}
          statuses={statuses}
          tags={tags}
          customColumns={customColumns}
          accent={accent}
          isDark={isDark}
          surface={surface}
          text={text}
          subText={subText}
          border={border}
          inputBg={inputBg}
          onSetTab={on.setSettingsTab}
          onAddStatus={on.addStatus}
          onAddTag={on.addTag}
          onAddCustomColumn={on.addCustomColumn}
          onClose={on.closeSettings}
          projectName={activeList?.name ?? "プロジェクト"}
          autoReminderForm={autoReminderForm}
          autoReminderSaved={autoReminderSaved}
          onToggleAutoReminder={on.toggleAutoReminder}
          onUpdateAutoReminderForm={on.updateAutoReminderForm}
          onSaveAutoReminder={on.saveAutoReminder}
        />
      )}

      {/* ===== 表示するカスタム項目ダイアログ ===== */}
      {customDisplay?.open && on.closeCustomDisplay && on.saveCustomDisplay && (
        <CustomDisplayDialog
          customColumns={customColumns}
          initialVisibleIds={visibleCustomColumnIds}
          accent={accent}
          isDark={isDark}
          surface={surface}
          text={text}
          subText={subText}
          border={border}
          onClose={on.closeCustomDisplay}
          onSave={on.saveCustomDisplay}
        />
      )}

      {/* ===== 自動作成ルール設定ダイアログ（繰り返しチュートリアル用） ===== */}
      {recurrenceDialog?.open &&
        on.saveRecurrence &&
        on.closeRecurrenceDialog &&
        on.showRecurrenceForm && (
          <RecurrenceIntroDialog
            showForm={recurrenceDialog.showForm}
            rule={recurrenceRule ?? null}
            accent={accent}
            isDark={isDark}
            surface={surface}
            text={text}
            subText={subText}
            border={border}
            inputBg={inputBg}
            chipBg={chipBg}
            onClose={on.closeRecurrenceDialog}
            onShowForm={on.showRecurrenceForm}
            onSave={on.saveRecurrence}
            onOpenList={on.openRecurrenceList}
          />
        )}

      {/* ===== 繰り返し作成一覧ダイアログ（繰り返しチュートリアル用） ===== */}
      {recurrenceListOpen && on.closeRecurrenceList && (
        <RecurrenceListDialog
          rule={recurrenceRule ?? null}
          projectName={activeList?.name ?? "プロジェクト"}
          assignee={selectedTask?.assignees[0] ?? "あなた"}
          accent={accent}
          isDark={isDark}
          surface={surface}
          text={text}
          subText={subText}
          border={border}
          inputBg={inputBg}
          chipBg={chipBg}
          onClose={on.closeRecurrenceList}
          onEditPriority={on.editRulePriority}
          onEditMemo={on.editRuleMemo}
        />
      )}

      {/* ===== 自動作成設定の更新プロンプト（繰り返しチュートリアル用） ===== */}
      {updatePrompt?.open && on.dismissUpdatePrompt && (
        <RecurrenceUpdatePromptToast
          isOriginal={updatePrompt.isOriginal}
          accent={accent}
          isDark={isDark}
          surface={surface}
          text={text}
          subText={subText}
          border={border}
          onConfirm={on.confirmUpdatePrompt ?? on.dismissUpdatePrompt}
          onDismiss={on.dismissUpdatePrompt}
        />
      )}

      {/* ===== リマインダーダイアログ（リマインダーチュートリアル用） ===== */}
      {reminderDialog?.open &&
        selectedTask &&
        on.closeReminderDialog &&
        on.addReminder && (
          <ReminderDialog
            reminders={selectedTask.reminders ?? []}
            deadline={selectedTask.endDate ?? selectedTask.startDate}
            accent={accent}
            isDark={isDark}
            surface={surface}
            text={text}
            subText={subText}
            border={border}
            inputBg={inputBg}
            chipBg={chipBg}
            onClose={on.closeReminderDialog}
            onAdd={on.addReminder}
            onDelete={on.deleteReminder}
          />
        )}
    </div>
  );
};

// ===== 表示するカスタム項目ダイアログ（実画面 TaskItemCustomColumnDialog を簡略再現） =====

interface CustomDisplayItem {
  id: number;
  label: string;
  isVisible: boolean;
}

interface CustomDisplayDialogProps {
  customColumns: SandboxCustomColumn[];
  initialVisibleIds: number[];
  accent: string;
  isDark: boolean;
  surface: string;
  text: string;
  subText: string;
  border: string;
  onClose: () => void;
  onSave: (visibleIds: number[]) => void;
}

const CustomDisplayDialog = ({
  customColumns,
  initialVisibleIds,
  accent,
  isDark,
  surface,
  text,
  subText,
  border,
  onClose,
  onSave,
}: CustomDisplayDialogProps) => {
  // 表示中の列を保存順で先頭に、未選択の列を定義順で末尾に並べる
  const [items, setItems] = useState<CustomDisplayItem[]>(() => {
    const byId = new Map(customColumns.map((c) => [c.id, c]));
    const visible = initialVisibleIds
      .map((id) => byId.get(id))
      .filter((c): c is SandboxCustomColumn => !!c)
      .map((c) => ({ id: c.id, label: c.name, isVisible: true }));
    const visibleSet = new Set(initialVisibleIds);
    const hidden = customColumns
      .filter((c) => !visibleSet.has(c.id))
      .map((c) => ({ id: c.id, label: c.name, isVisible: false }));
    return [...visible, ...hidden];
  });

  const visibleCount = items.filter((i) => i.isVisible).length;

  const move = (index: number, dir: -1 | 1) => {
    setItems((prev) => {
      const j = index + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[j]] = [next[j], next[index]];
      return next;
    });
  };

  const toggle = (index: number) =>
    setItems((prev) =>
      prev.map((it, i) =>
        i === index ? { ...it, isVisible: !it.isVisible } : it,
      ),
    );

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 60,
        padding: 16,
      }}
    >
      <div
        data-tut="custom-display-dialog"
        style={{
          width: "100%",
          maxWidth: 400,
          maxHeight: "80vh",
          backgroundColor: surface,
          borderRadius: 12,
          border: `2px solid ${isDark ? "#000" : "#fff"}`,
          boxShadow: "0 12px 40px rgba(0,0,0,0.4)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* ヘッダー */}
        <div
          style={{
            background: `linear-gradient(135deg, ${accent}, ${accent}CC)`,
            color: "#fff",
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ExtensionTwoToneIcon style={{ fontSize: 18 }} />
            <span style={{ fontWeight: 700, fontSize: "0.9rem" }}>
              表示するカスタム項目
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="閉じる"
            style={{ color: "#fff", display: "flex", padding: 2 }}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        {/* 本文 */}
        <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
          <p style={{ fontSize: "0.75rem", color: subText, marginBottom: 12 }}>
            タスクの「担当・期限」の下に表示するカスタム項目を選びます。1行に2項目まで表示され、並び順はこの一覧の順番になります。
          </p>
          {items.map((item, index) => (
            <div
              key={item.id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                padding: "8px 10px",
                borderRadius: 8,
                border: `1px solid ${border}`,
                marginBottom: 6,
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  cursor: "pointer",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <input
                  type="checkbox"
                  checked={item.isVisible}
                  onChange={() => toggle(index)}
                  style={{ accentColor: accent, flexShrink: 0 }}
                />
                <span
                  style={{
                    fontSize: "0.85rem",
                    color: item.isVisible ? text : subText,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.label}
                </span>
              </label>
              <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
                <button
                  type="button"
                  onClick={() => move(index, -1)}
                  disabled={index === 0}
                  aria-label="上へ移動"
                  style={{
                    color: subText,
                    padding: 4,
                    opacity: index === 0 ? 0.2 : 1,
                  }}
                >
                  <ArrowUpwardIcon style={{ fontSize: 16 }} />
                </button>
                <button
                  type="button"
                  onClick={() => move(index, 1)}
                  disabled={index === items.length - 1}
                  aria-label="下へ移動"
                  style={{
                    color: subText,
                    padding: 4,
                    opacity: index === items.length - 1 ? 0.2 : 1,
                  }}
                >
                  <ArrowDownwardIcon style={{ fontSize: 16 }} />
                </button>
              </div>
            </div>
          ))}
          <p style={{ fontSize: "0.75rem", color: subText, marginTop: 8 }}>
            表示中: {visibleCount} 項目
          </p>
        </div>

        {/* フッター */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            padding: 14,
            borderTop: `1px solid ${border}`,
            flexShrink: 0,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              fontWeight: 700,
              color: text,
              border: `1px solid ${border}`,
              backgroundColor: surface,
            }}
          >
            キャンセル
          </button>
          <button
            type="button"
            onClick={() => {
              onSave(items.filter((i) => i.isVisible).map((i) => i.id));
              onClose();
            }}
            style={{
              padding: "8px 20px",
              borderRadius: 8,
              fontWeight: 700,
              color: "#fff",
              backgroundColor: accent,
            }}
          >
            保存する
          </button>
        </div>
      </div>
    </div>
  );
};

// ===== 自動作成ルール設定ダイアログ（実画面 TaskDetailPanel の繰り返し設定ダイアログを簡略再現） =====
//
// 実画面と同じく 2 段構え:
//   1. まず「このタスクをもとに作成するルール」の一覧＋説明を表示する導入画面。
//   2. 「ルールを追加」を押すと、頻度（毎日・毎週の曜日など）を選ぶフォームを表示する。

/** 頻度の種別 */
type RecurrenceKind = "Daily" | "Weekly" | "Monthly" | "Yearly";
const RECURRENCE_WEEKDAYS = ["日", "月", "火", "水", "木", "金", "土"] as const;
/** 1〜31日 + 最終日(32) */
const RECURRENCE_MONTH_DAYS = [
  ...Array.from({ length: 31 }, (_, i) => i + 1),
  32,
];

/** 選択中の頻度から実画面 formatRecurrenceText と同じ要約文字列を作る */
const buildRecurrenceSummary = (
  kind: RecurrenceKind,
  weekday: number,
  intervalMonths: number,
  monthDay: number,
  yearMonth: number,
): string => {
  if (kind === "Daily") return "毎日";
  if (kind === "Weekly") return `毎週${RECURRENCE_WEEKDAYS[weekday]}曜日`;
  if (kind === "Monthly") {
    const head = intervalMonths <= 1 ? "毎月" : `${intervalMonths}ヶ月ごとの`;
    const day = monthDay === 32 ? "最終" : monthDay;
    return `${head}${day}日`;
  }
  const day = monthDay === 32 ? "最終" : monthDay;
  return `毎年${yearMonth}月${day}日`;
};

interface RecurrenceIntroDialogProps {
  /** true で頻度設定フォーム、false で導入（ルール一覧）画面を表示 */
  showForm: boolean;
  /** 設定済みのルール（未設定なら null） */
  rule: { title: string; summary: string } | null;
  accent: string;
  isDark: boolean;
  surface: string;
  text: string;
  subText: string;
  border: string;
  inputBg: string;
  chipBg: string;
  onClose: () => void;
  onShowForm: () => void;
  onSave: (frequency: string) => void;
  onOpenList?: () => void;
}

const RecurrenceIntroDialog = ({
  showForm,
  rule,
  accent,
  isDark,
  surface,
  text,
  subText,
  border,
  inputBg,
  chipBg,
  onClose,
  onShowForm,
  onSave,
  onOpenList,
}: RecurrenceIntroDialogProps) => {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 60,
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        data-tut="recurrence-dialog"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 440,
          maxHeight: "86vh",
          backgroundColor: surface,
          borderRadius: 12,
          border: `2px solid ${isDark ? "#000" : "#fff"}`,
          boxShadow: "0 12px 40px rgba(0,0,0,0.4)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* ヘッダー（実画面同様グラデーション） */}
        <div
          style={{
            background: `linear-gradient(135deg, ${accent}, ${accent}CC)`,
            color: "#fff",
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <AutorenewIcon style={{ fontSize: 18 }} />
            <span style={{ fontWeight: 700, fontSize: "0.9rem" }}>
              自動作成ルール設定
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="閉じる"
            style={{ color: "#fff", display: "flex", padding: 2 }}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        {/* 本文 */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {!showForm ? (
            <>
              {/* 説明文: 設定時点のタスク内容がコピーされることを明示 */}
              <div
                style={{
                  margin: "16px 16px 0",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                  borderRadius: 8,
                  padding: 12,
                  fontSize: "0.75rem",
                  lineHeight: 1.7,
                  color: subText,
                  backgroundColor: `${accent}1A`,
                }}
              >
                <InfoOutlinedIcon
                  style={{ fontSize: 16, color: accent, flexShrink: 0, marginTop: 2 }}
                />
                <span>
                  <strong style={{ color: text }}>設定した時点のタスク内容</strong>
                  （タイトル・メモ・担当者・サブタスクなど）が保存され、指定した頻度ごとにその内容をもとに新しいタスクが作成されます。
                </span>
              </div>

              {/* このタスクをもとに作成されたルール */}
              <div style={{ padding: 16 }}>
                <div
                  style={{
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                    color: subText,
                    marginBottom: 8,
                  }}
                >
                  このタスクをもとに作成されたルール
                </div>
                {rule ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                      padding: "8px 10px",
                      borderRadius: 8,
                      backgroundColor: chipBg,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                      <AutorenewIcon
                        style={{ fontSize: 18, color: accent, opacity: 0.8, flexShrink: 0 }}
                      />
                      <span
                        style={{
                          fontSize: "0.85rem",
                          color: text,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {rule.summary}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      fontSize: "0.85rem",
                      color: subText,
                      textAlign: "center",
                      padding: "12px 0",
                    }}
                  >
                    繰り返しルールはまだ設定されていません
                  </div>
                )}
              </div>

              {/* ルールを追加ボタン */}
              <div style={{ padding: "0 16px 16px" }}>
                <button
                  type="button"
                  data-tut="recurrence-add-rule-btn"
                  onClick={onShowForm}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                    padding: "8px 0",
                    borderRadius: 8,
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    color: accent,
                    border: `1px solid ${accent}80`,
                    backgroundColor: "transparent",
                  }}
                >
                  <AddIcon style={{ fontSize: 18 }} />
                  ルールを追加
                </button>
              </div>

              {/* 全ての自動作成ルールを表示 */}
              {onOpenList && (
                <div style={{ padding: "0 16px 16px" }}>
                  <button
                    type="button"
                    onClick={() => {
                      onOpenList();
                      onClose();
                    }}
                    style={{
                      width: "100%",
                      padding: "8px 0",
                      borderRadius: 8,
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      color: subText,
                      border: `1px solid ${border}`,
                      backgroundColor: surface,
                    }}
                  >
                    全ての自動作成ルールを表示
                  </button>
                </div>
              )}
            </>
          ) : (
            <RecurrenceFrequencyForm
              accent={accent}
              isDark={isDark}
              surface={surface}
              text={text}
              subText={subText}
              border={border}
              inputBg={inputBg}
              onCancel={onClose}
              onSave={onSave}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ===== 頻度設定フォーム（実画面 RecurrenceDialog の頻度セクションを再現） =====

interface RecurrenceFrequencyFormProps {
  accent: string;
  isDark: boolean;
  surface: string;
  text: string;
  subText: string;
  border: string;
  inputBg: string;
  onCancel: () => void;
  onSave: (frequency: string) => void;
}

const RecurrenceFrequencyForm = ({
  accent,
  isDark,
  surface,
  text,
  subText,
  border,
  inputBg,
  onCancel,
  onSave,
}: RecurrenceFrequencyFormProps) => {
  const [kind, setKind] = useState<RecurrenceKind>("Weekly");
  const [weekday, setWeekday] = useState(1); // 月曜
  const [intervalMonths, setIntervalMonths] = useState(1);
  const [monthDay, setMonthDay] = useState(1);
  const [yearMonth, setYearMonth] = useState(1);

  const infoBg = isDark ? "#1b2733" : "#EBF4FF";
  const infoBorder = isDark ? "#274057" : "#cfe2ff";

  const selectStyle: React.CSSProperties = {
    padding: "4px 6px",
    border: `1px solid ${border}`,
    borderRadius: 6,
    fontSize: "0.8rem",
    backgroundColor: inputBg,
    color: text,
  };

  const handleSave = () =>
    onSave(
      buildRecurrenceSummary(kind, weekday, intervalMonths, monthDay, yearMonth),
    );

  return (
    <div
      style={{ padding: 20, display: "flex", flexDirection: "column", gap: 18 }}
    >
      {/* 区切り「新しいルールを追加」 */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          color: subText,
        }}
      >
        <div style={{ flex: 1, borderTop: `1px solid ${border}` }} />
        <span style={{ fontSize: "0.75rem", fontWeight: 600 }}>
          新しいルールを追加
        </span>
        <div style={{ flex: 1, borderTop: `1px solid ${border}` }} />
      </div>

      <div>
        <p
          style={{
            fontSize: "0.85rem",
            fontWeight: 700,
            color: text,
            marginBottom: 10,
          }}
        >
          繰り返し作成の頻度
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {/* 毎日 */}
          <RecurrenceFrequencyOption
            label="毎日"
            checked={kind === "Daily"}
            onSelect={() => setKind("Daily")}
            accent={accent}
            text={text}
          />
          {/* 毎週（曜日） */}
          <RecurrenceFrequencyOption
            label="毎週"
            checked={kind === "Weekly"}
            onSelect={() => setKind("Weekly")}
            accent={accent}
            text={text}
          >
            <select
              value={weekday}
              onChange={(e) => setWeekday(Number(e.target.value))}
              onClick={(e) => e.stopPropagation()}
              disabled={kind !== "Weekly"}
              style={{ ...selectStyle, opacity: kind === "Weekly" ? 1 : 0.5 }}
            >
              {RECURRENCE_WEEKDAYS.map((d, i) => (
                <option key={d} value={i}>
                  {d}曜日
                </option>
              ))}
            </select>
          </RecurrenceFrequencyOption>
          {/* 毎月（間隔 + 日） */}
          <RecurrenceFrequencyOption
            label=""
            checked={kind === "Monthly"}
            onSelect={() => setKind("Monthly")}
            accent={accent}
            text={text}
          >
            <select
              value={intervalMonths}
              onChange={(e) => setIntervalMonths(Number(e.target.value))}
              onClick={(e) => e.stopPropagation()}
              disabled={kind !== "Monthly"}
              style={{ ...selectStyle, opacity: kind === "Monthly" ? 1 : 0.5 }}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  {m === 1 ? "毎月" : `${m}ヶ月ごと`}
                </option>
              ))}
            </select>
            <select
              value={monthDay}
              onChange={(e) => setMonthDay(Number(e.target.value))}
              onClick={(e) => e.stopPropagation()}
              disabled={kind !== "Monthly"}
              style={{ ...selectStyle, opacity: kind === "Monthly" ? 1 : 0.5 }}
            >
              {RECURRENCE_MONTH_DAYS.map((d) => (
                <option key={d} value={d}>
                  {d === 32 ? "最終日" : `${d}日`}
                </option>
              ))}
            </select>
          </RecurrenceFrequencyOption>
          {/* 毎年（月 + 日） */}
          <RecurrenceFrequencyOption
            label="毎年"
            checked={kind === "Yearly"}
            onSelect={() => setKind("Yearly")}
            accent={accent}
            text={text}
          >
            <select
              value={yearMonth}
              onChange={(e) => setYearMonth(Number(e.target.value))}
              onClick={(e) => e.stopPropagation()}
              disabled={kind !== "Yearly"}
              style={{ ...selectStyle, opacity: kind === "Yearly" ? 1 : 0.5 }}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  {m}月
                </option>
              ))}
            </select>
            <select
              value={monthDay}
              onChange={(e) => setMonthDay(Number(e.target.value))}
              onClick={(e) => e.stopPropagation()}
              disabled={kind !== "Yearly"}
              style={{ ...selectStyle, opacity: kind === "Yearly" ? 1 : 0.5 }}
            >
              {RECURRENCE_MONTH_DAYS.map((d) => (
                <option key={d} value={d}>
                  {d === 32 ? "最終日" : `${d}日`}
                </option>
              ))}
            </select>
          </RecurrenceFrequencyOption>
        </div>
      </div>

      {/* 期限・リマインダー・通知の補足（簡易版のため説明のみ） */}
      <div
        style={{
          backgroundColor: infoBg,
          border: `1px solid ${infoBorder}`,
          borderRadius: 8,
          padding: 12,
          fontSize: "0.78rem",
          lineHeight: 1.7,
          color: text,
        }}
      >
        <p style={{ fontWeight: 700, marginBottom: 4 }}>
          このほかに設定できること
        </p>
        自動作成されるタスクの「期限」「リマインダー」「作成時の通知」も、実際の画面ではこの下でまとめて設定できます。
      </div>

      {/* ボタン */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button
          type="button"
          onClick={onCancel}
          style={{
            padding: "8px 16px",
            borderRadius: 8,
            fontWeight: 700,
            color: text,
            border: `1px solid ${border}`,
            backgroundColor: surface,
          }}
        >
          キャンセル
        </button>
        <button
          type="button"
          data-tut="recurrence-dialog-save"
          onClick={handleSave}
          style={{
            padding: "8px 20px",
            borderRadius: 8,
            fontWeight: 700,
            color: "#fff",
            backgroundColor: accent,
          }}
        >
          保存する
        </button>
      </div>
    </div>
  );
};

/** 頻度フォームのラジオ行（ラベル＋任意の選択 UI） */
const RecurrenceFrequencyOption = ({
  label,
  checked,
  onSelect,
  accent,
  text,
  children,
}: {
  label: string;
  checked: boolean;
  onSelect: () => void;
  accent: string;
  text: string;
  children?: React.ReactNode;
}) => (
  <div
    onClick={onSelect}
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      minHeight: 34,
      cursor: "pointer",
    }}
  >
    <span
      style={{
        width: 16,
        height: 16,
        borderRadius: 9999,
        border: `2px solid ${checked ? accent : "#9ca3af"}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {checked && (
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: 9999,
            backgroundColor: accent,
          }}
        />
      )}
    </span>
    {label && (
      <span
        style={{
          fontSize: "0.85rem",
          fontWeight: 600,
          color: checked ? accent : text,
        }}
      >
        {label}
      </span>
    )}
    {children && (
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {children}
      </div>
    )}
  </div>
);

// ===== 繰り返し作成一覧ダイアログ（実画面 RecurrenceManagementDialog を簡略再現） =====
//
// 実画面同様、右からスライドインする 2 ペイン構成:
//   左ペイン … プロジェクトごとにグループ化されたルール一覧（検索フィルター付き）
//   右ペイン … 選択中ルールのタスク内容（編集パネル）

interface RecurrenceListDialogProps {
  rule: {
    title: string;
    summary: string;
    priority: SandboxPriority;
    memo: string;
  } | null;
  projectName: string;
  /** 選択中ルールの担当者表示名 */
  assignee: string;
  accent: string;
  isDark: boolean;
  surface: string;
  text: string;
  subText: string;
  border: string;
  inputBg: string;
  chipBg: string;
  onClose: () => void;
  /** 詳細ペインで重要度を変更する（チュートリアルの修正フェーズ用） */
  onEditPriority?: (p: SandboxPriority) => void;
  /** 詳細ペインでメモを変更する */
  onEditMemo?: (v: string) => void;
}

const RecurrenceListDialog = ({
  rule,
  projectName,
  assignee,
  accent,
  isDark,
  surface,
  text,
  subText,
  border,
  inputBg,
  chipBg,
  onClose,
  onEditPriority,
  onEditMemo,
}: RecurrenceListDialogProps) => {
  const listBg = isDark ? "#181818" : "#f9fafb";
  const groupBg = isDark ? "#2a2a2a" : "#f3f4f6";
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        justifyContent: "flex-end",
        zIndex: 60,
      }}
      onClick={onClose}
    >
      <div
        data-tut="recurrence-list-dialog"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(768px, 100%)",
          height: "100%",
          backgroundColor: surface,
          borderLeft: `2px solid ${border}`,
          boxShadow: "-8px 0 24px rgba(0,0,0,0.25)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* ヘッダー */}
        <div
          style={{
            padding: "14px 16px",
            borderBottom: `1px solid ${border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontWeight: 700,
              fontSize: "1rem",
              color: text,
            }}
          >
            <AutorenewIcon style={{ fontSize: 20, color: accent }} />
            繰り返し作成一覧
          </span>
          <button
            type="button"
            data-tut="recurrence-list-close"
            onClick={onClose}
            aria-label="閉じる"
            style={{ color: subText, display: "flex", padding: 2 }}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        {/* メインエリア（2 ペイン） */}
        <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
          {/* ===== 左ペイン: ルール一覧 ===== */}
          <div
            style={{
              width: "33%",
              minWidth: 0,
              borderRight: `1px solid ${border}`,
              backgroundColor: listBg,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* 検索フィルター（装飾） */}
            <div
              style={{
                flexShrink: 0,
                borderBottom: `1px solid ${border}`,
                display: "flex",
                alignItems: "center",
                gap: 4,
                padding: "8px 12px",
                fontSize: "0.75rem",
                color: subText,
              }}
            >
              <FilterListIcon style={{ fontSize: 16 }} />
              検索フィルター
            </div>

            <div style={{ flex: 1, overflowY: "auto" }}>
              {!rule ? (
                <p
                  style={{
                    fontSize: "0.8rem",
                    color: subText,
                    textAlign: "center",
                    padding: 16,
                  }}
                >
                  繰り返し作成ルールが設定されていません
                </p>
              ) : (
                <>
                  {/* プロジェクトグループ見出し */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "10px 12px",
                      backgroundColor: groupBg,
                      borderBottom: `1px solid ${border}`,
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      color: text,
                    }}
                  >
                    <ExpandMoreIcon style={{ fontSize: 18, color: subText }} />
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {projectName}
                    </span>
                    <span style={{ color: subText, fontWeight: 400, flexShrink: 0 }}>
                      (1)
                    </span>
                  </div>

                  {/* ルール行（選択済み・左に色バー） */}
                  <div
                    style={{
                      display: "flex",
                      borderBottom: `1px solid ${border}`,
                      backgroundColor: `${accent}14`,
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ width: 4, backgroundColor: accent, flexShrink: 0 }} />
                    <div style={{ flex: 1, padding: 12, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: "0.85rem",
                          fontWeight: 600,
                          color: text,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {rule.title}
                      </div>
                      <div
                        style={{
                          marginTop: 4,
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          color: accent,
                        }}
                      >
                        <AutorenewIcon style={{ fontSize: 14 }} />
                        {rule.summary}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ===== 右ペイン: 選択中ルールの内容（編集パネル） ===== */}
          <div style={{ flex: 1, minWidth: 0, overflowY: "auto" }}>
            {!rule ? (
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: subText,
                  fontSize: "0.85rem",
                  padding: 16,
                  textAlign: "center",
                }}
              >
                繰り返し作成ルールを選択してください
              </div>
            ) : (
              <div
                data-tut="rule-detail-panel"
                style={{
                  padding: 24,
                  display: "flex",
                  flexDirection: "column",
                  gap: 16,
                }}
              >
                {/* タイトル（実画面同様の下線付き入力風） */}
                <input
                  type="text"
                  readOnly
                  value={rule.title}
                  style={{
                    fontSize: "1.25rem",
                    fontWeight: 700,
                    color: text,
                    width: "100%",
                    border: "none",
                    borderBottom: `2px solid ${border}`,
                    outline: "none",
                    backgroundColor: "transparent",
                    paddingBottom: 4,
                  }}
                />

                {/* このタスクをもとに作成されたルール（頻度＋修正/削除） */}
                <div>
                  <div
                    style={{
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                      color: subText,
                      marginBottom: 6,
                    }}
                  >
                    このタスクをもとに作成されたルール
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                      padding: "8px 10px",
                      borderRadius: 8,
                      backgroundColor: chipBg,
                    }}
                  >
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: "0.82rem",
                        fontWeight: 700,
                        color: accent,
                        minWidth: 0,
                      }}
                    >
                      <AutorenewIcon style={{ fontSize: 16, opacity: 0.85 }} />
                      {rule.summary}
                    </span>
                    <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                      <span style={{ fontSize: "0.75rem", padding: "2px 8px", color: accent }}>
                        修正
                      </span>
                      <span style={{ fontSize: "0.75rem", padding: "2px 8px", color: "#F87171" }}>
                        削除
                      </span>
                    </div>
                  </div>
                </div>

                {/* 担当者 */}
                <div>
                  <SectionLabel
                    icon={
                      <PeopleTwoToneIcon style={{ fontSize: 16, color: subText }} />
                    }
                    label="担当者 (1名)"
                    subText={subText}
                  />
                  <div
                    style={{
                      marginTop: 4,
                      minHeight: 40,
                      padding: 6,
                      border: `1px solid ${border}`,
                      borderRadius: 6,
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <span
                      style={{
                        fontSize: "0.75rem",
                        padding: "2px 8px",
                        backgroundColor: chipBg,
                        borderRadius: 9999,
                        color: text,
                      }}
                    >
                      {assignee}
                    </span>
                  </div>
                </div>

                {/* 重要度（編集可能・修正フェーズの対象） */}
                <div data-tut="rule-detail-priority">
                  <SectionLabel
                    icon={
                      <LabelImportantTwoToneIcon
                        style={{ fontSize: 16, color: subText }}
                      />
                    }
                    label="重要度"
                    subText={subText}
                  />
                  <div style={{ marginTop: 4 }}>
                    <PriorityButtons
                      value={rule.priority}
                      text={text}
                      subText={subText}
                      isDark={isDark}
                      onChange={(p) => onEditPriority?.(p)}
                    />
                  </div>
                </div>

                {/* メモ（編集可能） */}
                <div>
                  <SectionLabel
                    icon={
                      <NoteAltTwoToneIcon style={{ fontSize: 16, color: subText }} />
                    }
                    label="メモ"
                    subText={subText}
                  />
                  <textarea
                    value={rule.memo}
                    onChange={(e) => onEditMemo?.(e.target.value)}
                    placeholder="メモを入力..."
                    style={{
                      marginTop: 4,
                      width: "100%",
                      minHeight: 64,
                      padding: 8,
                      border: `2px solid ${border}`,
                      borderRadius: 8,
                      outline: "none",
                      resize: "vertical",
                      backgroundColor: inputBg,
                      color: text,
                      fontSize: "0.85rem",
                    }}
                  />
                </div>

                {/* 説明 */}
                <div
                  style={{
                    marginTop: 4,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 8,
                    borderRadius: 8,
                    padding: 12,
                    fontSize: "0.78rem",
                    lineHeight: 1.7,
                    color: subText,
                    backgroundColor: `${accent}1A`,
                  }}
                >
                  <InfoOutlinedIcon
                    style={{ fontSize: 16, color: accent, flexShrink: 0, marginTop: 2 }}
                  />
                  <span>
                    ここで<strong style={{ color: text }}>タイトル・担当者・タグ・重要度・メモや頻度</strong>を編集できます。変更内容は、以降に自動作成されるタスクへ反映されます。
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ===== 自動作成設定の更新プロンプト（実画面 RecurrenceUpdatePrompt を簡略再現） =====

interface RecurrenceUpdatePromptToastProps {
  isOriginal: boolean;
  accent: string;
  isDark: boolean;
  surface: string;
  text: string;
  subText: string;
  border: string;
  onConfirm: () => void;
  onDismiss: () => void;
}

const RecurrenceUpdatePromptToast = ({
  isOriginal,
  accent,
  isDark,
  surface,
  text,
  subText,
  border,
  onConfirm,
  onDismiss,
}: RecurrenceUpdatePromptToastProps) => {
  const message = isOriginal
    ? "自動作成設定のもとになったタスクが編集されました。同様の内容で自動作成設定も更新しますか？"
    : "自動作成によって作られたタスクが編集されました。同様の内容で自動作成設定も更新しますか？";
  return (
    <div
      data-tut="update-prompt"
      style={{
        position: "absolute",
        bottom: 24,
        right: 24,
        width: "min(360px, calc(100% - 48px))",
        backgroundColor: surface,
        border: `1px solid ${border}`,
        borderRadius: 10,
        boxShadow: "0 12px 32px rgba(0,0,0,0.3)",
        padding: 16,
        zIndex: 70,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <AutorenewIcon
          style={{ fontSize: 20, color: accent, flexShrink: 0, marginTop: 2 }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: "0.82rem", lineHeight: 1.6, color: text }}>
            {message}
          </p>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button
              type="button"
              onClick={onConfirm}
              style={{
                padding: "6px 14px",
                borderRadius: 6,
                fontSize: "0.78rem",
                fontWeight: 700,
                color: "#fff",
                backgroundColor: accent,
              }}
            >
              更新する
            </button>
            <button
              type="button"
              onClick={onDismiss}
              style={{
                padding: "6px 14px",
                borderRadius: 6,
                fontSize: "0.78rem",
                fontWeight: 700,
                color: text,
                border: `1px solid ${border}`,
                backgroundColor: isDark ? "#2a2a2a" : "#f3f4f6",
              }}
            >
              しない
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="閉じる"
          style={{ color: subText, display: "flex", padding: 2, flexShrink: 0 }}
        >
          <CloseIcon fontSize="small" />
        </button>
      </div>
    </div>
  );
};

// ===== リマインダーダイアログ（実画面 TaskDetailPanel のリマインダーを簡略再現） =====

interface ReminderDialogProps {
  reminders: SandboxReminder[];
  /** 追加時の日付の初期値（YYYY-MM-DD）。タスクの期限があればそれを既定にする */
  deadline?: string;
  accent: string;
  isDark: boolean;
  surface: string;
  text: string;
  subText: string;
  border: string;
  inputBg: string;
  chipBg: string;
  onClose: () => void;
  onAdd: (label: string) => void;
  onDelete?: (id: string) => void;
}

/** 追加フォームの時刻の選択肢（実画面同様に 08:00 を既定にする） */
const REMINDER_TIME_OPTIONS = [
  "08:00",
  "09:00",
  "10:00",
  "12:00",
  "15:00",
  "18:00",
];

const ReminderDialog = ({
  reminders,
  deadline,
  accent,
  isDark,
  surface,
  text,
  subText,
  border,
  inputBg,
  chipBg,
  onClose,
  onAdd,
  onDelete,
}: ReminderDialogProps) => {
  const [date, setDate] = useState(deadline ?? "");
  const [time, setTime] = useState("08:00");

  const inputStyle: React.CSSProperties = {
    padding: 8,
    border: `1px solid ${border}`,
    borderRadius: 6,
    outline: "none",
    backgroundColor: inputBg,
    color: text,
    fontSize: "0.85rem",
  };

  const handleAdd = () => {
    if (!date) return;
    onAdd(`${formatYmd(date)} ${time}`);
  };

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 60,
        padding: 16,
      }}
    >
      <div
        data-tut="reminder-dialog"
        style={{
          width: "100%",
          maxWidth: 448,
          backgroundColor: surface,
          borderRadius: 12,
          border: `2px solid ${isDark ? "#000" : "#fff"}`,
          boxShadow: "0 12px 40px rgba(0,0,0,0.4)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* ヘッダー */}
        <div
          style={{
            background: `linear-gradient(135deg, ${accent}, ${accent}CC)`,
            color: "#fff",
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <AccessAlarmIcon style={{ fontSize: 18 }} />
            <span style={{ fontWeight: 700, fontSize: "0.9rem" }}>
              リマインダー
            </span>
          </div>
          <button
            type="button"
            data-tut="reminder-close"
            onClick={onClose}
            aria-label="閉じる"
            style={{ color: "#fff", display: "flex", padding: 2 }}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        {/* ボディ */}
        <div
          style={{
            padding: 16,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          {/* 既存のリマインダー一覧 */}
          <div
            data-tut="reminder-list"
            style={{ display: "flex", flexDirection: "column", gap: 8 }}
          >
            {reminders.length === 0 ? (
              <div
                style={{
                  fontSize: "0.85rem",
                  color: subText,
                  textAlign: "center",
                  padding: "8px 0",
                }}
              >
                リマインダーはまだ設定されていません
              </div>
            ) : (
              reminders.map((r) => (
                <div
                  key={r.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: 8,
                    borderRadius: 8,
                    backgroundColor: chipBg,
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <AccessAlarmIcon
                      style={{ fontSize: 17, color: accent }}
                    />
                    <span style={{ fontSize: "0.85rem", color: text }}>
                      {r.label}
                    </span>
                    {r.isAuto && (
                      <span
                        style={{
                          fontSize: "0.65rem",
                          lineHeight: 1,
                          padding: "2px 6px",
                          borderRadius: 4,
                          border: `1px solid ${accent}`,
                          color: accent,
                          fontWeight: 600,
                        }}
                        title="プロジェクトの自動リマインダー設定で作成されました"
                      >
                        自動
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => onDelete?.(r.id)}
                    aria-label="削除"
                    title="削除"
                    style={{
                      display: "flex",
                      padding: 4,
                      borderRadius: 6,
                      color: "#F85B52",
                      cursor: onDelete ? "pointer" : "default",
                    }}
                  >
                    <DeleteOutlinedIcon style={{ fontSize: 17 }} />
                  </button>
                </div>
              ))
            )}
          </div>

          {/* リマインダーを追加 */}
          <div style={{ borderTop: `1px dashed ${border}`, paddingTop: 12 }}>
            <span
              style={{
                fontSize: "0.75rem",
                fontWeight: 600,
                color: accent,
              }}
            >
              リマインダーを追加
            </span>
            <div
              data-tut="reminder-add-row"
              style={{
                marginTop: 8,
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                style={inputStyle}
              />
              <select
                value={time}
                onChange={(e) => setTime(e.target.value)}
                style={inputStyle}
              >
                {REMINDER_TIME_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleAdd}
                disabled={!date}
                style={{
                  marginLeft: "auto",
                  padding: "8px 16px",
                  borderRadius: 6,
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  color: "#fff",
                  backgroundColor: date ? accent : subText,
                  cursor: date ? "pointer" : "default",
                }}
              >
                追加
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===== 全体設定ダイアログ（実画面 GeneralSettingsDialog を簡略再現） =====

const SETTINGS_PALETTE: string[] = [
  "#3989FA",
  "#40C063",
  "#FCA733",
  "#F85B52",
  "#FF80BD",
  "#D67EF6",
  "#6C73E7",
  "#69D5D8",
  "#77C80A",
  "#8A94A6",
];

interface SettingsDialogProps {
  tab: SettingsTab;
  statuses: SandboxStatusDef[];
  tags: SandboxTag[];
  customColumns: SandboxCustomColumn[];
  accent: string;
  isDark: boolean;
  surface: string;
  text: string;
  subText: string;
  border: string;
  inputBg: string;
  onSetTab?: (tab: SettingsTab) => void;
  onAddStatus?: (name: string, color: string) => void;
  onAddTag?: (name: string, color: string) => void;
  onAddCustomColumn?: (
    name: string,
    type: "text" | "select",
    options: string[],
  ) => void;
  onClose: () => void;
  // ===== 自動リマインダー（リマインダーチュートリアル用・任意） =====
  /** 対象プロジェクト名 */
  projectName?: string;
  /** 自動リマインダーフォーム。指定時のみ「自動リマインダー」タブを表示する */
  autoReminderForm?: SandboxAutoReminderForm;
  /** 自動リマインダー設定が保存済みか */
  autoReminderSaved?: boolean;
  onToggleAutoReminder?: () => void;
  onUpdateAutoReminderForm?: (
    updates: Partial<SandboxAutoReminderForm>,
  ) => void;
  onSaveAutoReminder?: () => void;
}

const SettingsDialog = ({
  tab,
  statuses,
  tags,
  customColumns,
  accent,
  isDark,
  surface,
  text,
  subText,
  border,
  inputBg,
  onSetTab,
  onAddStatus,
  onAddTag,
  onAddCustomColumn,
  onClose,
  projectName,
  autoReminderForm,
  autoReminderSaved,
  onToggleAutoReminder,
  onUpdateAutoReminderForm,
  onSaveAutoReminder,
}: SettingsDialogProps) => {
  const [statusName, setStatusName] = useState("");
  const [statusColor, setStatusColor] = useState(SETTINGS_PALETTE[6]);
  const [statusColorPickerOpen, setStatusColorPickerOpen] = useState(false);
  const [tagName, setTagName] = useState("");
  const [tagColor, setTagColor] = useState(SETTINGS_PALETTE[0]);
  const [columnName, setColumnName] = useState("");
  const [columnType, setColumnType] = useState<"text" | "select">("text");
  const [columnOptions, setColumnOptions] = useState<string[]>([""]);

  // カスタム項目の追加可否（名前必須・選択肢型なら有効な選択肢が1つ以上）
  const trimmedOptions = columnOptions.map((o) => o.trim()).filter(Boolean);
  const canAddColumn =
    columnName.trim().length > 0 &&
    (columnType === "text" || trimmedOptions.length > 0);

  const resetColumnForm = () => {
    setColumnName("");
    setColumnType("text");
    setColumnOptions([""]);
  };

  const infoBg = isDark ? "#1b2733" : "#EBF4FF";
  const infoBorder = isDark ? "#274057" : "#cfe2ff";
  const sectionBg = isDark ? "#2a2a2a" : "#f9fafb";

  const tabDefs: { key: SettingsTab; label: string }[] = [
    { key: "status", label: "ステータス設定" },
    { key: "tags", label: "タグ設定" },
    { key: "custom", label: "カスタム項目" },
    // 自動リマインダーフォームが渡されたときのみタブを追加する
    ...(autoReminderForm
      ? [{ key: "autoReminder" as SettingsTab, label: "自動リマインダー" }]
      : []),
  ];

  const inputStyle: React.CSSProperties = {
    flex: 1,
    padding: 8,
    border: `1px solid ${border}`,
    borderRadius: 6,
    outline: "none",
    backgroundColor: inputBg,
    color: text,
    fontSize: "0.85rem",
  };

  /**
   * タグ用の色パレット（実画面準拠）。
   * 全色を 5 列のグリッドで一覧表示し、選択中の色をリングで強調する。
   */
  const TagColorGrid = ({
    value,
    onPick,
  }: {
    value: string;
    onPick: (c: string) => void;
  }) => (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(5, 1fr)",
        gap: 8,
        justifyItems: "center",
        padding: 4,
      }}
    >
      {SETTINGS_PALETTE.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onPick(c)}
          aria-label={`色 ${c}`}
          style={{
            width: 32,
            height: 32,
            borderRadius: 9999,
            backgroundColor: c,
            border: "2px solid #fff",
            boxShadow:
              value === c
                ? `0 0 0 2px ${surface}, 0 0 0 4px ${accent}`
                : "0 1px 2px rgba(0,0,0,0.25)",
          }}
        />
      ))}
    </div>
  );

  /**
   * ステータス用の色スウォッチ（実画面準拠）。
   * 入力欄の隣に置く単一の丸ボタン。押すとパレットの開閉を切り替える。
   * パレット自体はチュートリアルのハイライト枠内に収まるよう、
   * ポップオーバーではなくセクション内へインライン展開する（呼び出し側で描画）。
   */
  const StatusColorSwatch = ({ value }: { value: string }) => (
    <button
      type="button"
      onClick={() => setStatusColorPickerOpen((o) => !o)}
      aria-label="ステータスの色を選択"
      style={{
        flexShrink: 0,
        width: 32,
        height: 32,
        borderRadius: 9999,
        backgroundColor: value,
        border: `1px solid ${border}`,
        boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
      }}
    />
  );

  const InfoBox = ({
    title,
    children,
  }: {
    title: string;
    children: React.ReactNode;
  }) => (
    <div
      style={{
        backgroundColor: infoBg,
        border: `1px solid ${infoBorder}`,
        borderRadius: 8,
        padding: 14,
        fontSize: "0.8rem",
        lineHeight: 1.7,
        color: text,
      }}
    >
      <p style={{ fontWeight: 700, marginBottom: 4 }}>{title}</p>
      {children}
    </div>
  );

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 60,
        padding: 16,
      }}
    >
      <div
        data-tut="settings-dialog"
        style={{
          width: "100%",
          maxWidth: 560,
          maxHeight: "86vh",
          backgroundColor: surface,
          borderRadius: 12,
          border: `2px solid ${isDark ? "#000" : "#fff"}`,
          boxShadow: "0 12px 40px rgba(0,0,0,0.4)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* ヘッダー */}
        <div
          style={{
            background: `linear-gradient(135deg, ${accent}, ${accent}CC)`,
            color: "#fff",
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <SettingsTwoToneIcon style={{ fontSize: 18 }} />
            <span style={{ fontWeight: 700, fontSize: "0.9rem" }}>全体設定</span>
          </div>
          <button
            type="button"
            data-tut="settings-close"
            onClick={onClose}
            aria-label="閉じる"
            style={{ color: "#fff", display: "flex", padding: 2 }}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        {/* タブバー */}
        <div
          data-tut="settings-tabs"
          style={{
            display: "flex",
            gap: 4,
            padding: "0 12px",
            borderBottom: `1px solid ${border}`,
            backgroundColor: sectionBg,
            flexShrink: 0,
          }}
        >
          {tabDefs.map((t) => {
            const active = t.key === tab;
            return (
              <button
                key={t.key}
                type="button"
                data-tut={`settings-tab-${t.key}`}
                onClick={() => onSetTab?.(t.key)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "10px 10px",
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  color: active ? accent : subText,
                  borderBottom: `2px solid ${active ? accent : "transparent"}`,
                  backgroundColor: active ? surface : "transparent",
                }}
              >
                {t.key === "status" && (
                  <FlagTwoToneIcon style={{ fontSize: 16 }} />
                )}
                {t.key === "tags" && (
                  <StyleTwoToneIcon style={{ fontSize: 16 }} />
                )}
                {t.key === "custom" && (
                  <ExtensionTwoToneIcon style={{ fontSize: 16 }} />
                )}
                {t.label}
              </button>
            );
          })}
        </div>

        {/* コンテンツ */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: 20,
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          {tab === "status" && (
            <>
              <InfoBox title="ステータスとは">
                タスクの進捗状態を表すラベルです。チャットルームごとに設定でき、同一ルーム内のプロジェクト間で共通です。
              </InfoBox>
              <div
                style={{ display: "flex", flexDirection: "column", gap: 8 }}
              >
                {statuses.map((s) => (
                  <div
                    key={s.name}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "8px 10px",
                      backgroundColor: sectionBg,
                      border: `1px solid ${border}`,
                      borderRadius: 6,
                    }}
                  >
                    <span
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: 9999,
                        backgroundColor: s.color,
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ fontWeight: 600, color: s.color }}>
                      {s.name}
                    </span>
                  </div>
                ))}
              </div>
              <div data-tut="settings-add-status">
                <p
                  style={{
                    fontSize: "0.8rem",
                    fontWeight: 700,
                    color: text,
                    marginBottom: 6,
                  }}
                >
                  新しいステータスを追加
                </p>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    padding: 12,
                    backgroundColor: sectionBg,
                    border: `1px solid ${border}`,
                    borderRadius: 8,
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <input
                      type="text"
                      value={statusName}
                      onChange={(e) => setStatusName(e.target.value)}
                      placeholder="ステータス名（例: レビュー中）"
                      style={inputStyle}
                    />
                    <StatusColorSwatch value={statusColor} />
                    <button
                      type="button"
                      onClick={() => {
                        if (!statusName.trim()) return;
                        onAddStatus?.(statusName.trim(), statusColor);
                        setStatusName("");
                        setStatusColorPickerOpen(false);
                      }}
                      disabled={!statusName.trim()}
                      style={{
                        padding: "8px 16px",
                        borderRadius: 6,
                        fontWeight: 700,
                        color: "#fff",
                        flexShrink: 0,
                        backgroundColor: statusName.trim() ? accent : "#cbd5e1",
                        cursor: statusName.trim() ? "pointer" : "not-allowed",
                      }}
                    >
                      追加
                    </button>
                  </div>
                  {/* パレットはハイライト枠内に収まるようインライン展開する */}
                  {statusColorPickerOpen && (
                    <TagColorGrid
                      value={statusColor}
                      onPick={(c) => {
                        setStatusColor(c);
                        setStatusColorPickerOpen(false);
                      }}
                    />
                  )}
                </div>
              </div>
            </>
          )}

          {tab === "tags" && (
            <>
              <InfoBox title="タグとは">
                タスクに付けられるラベルです。チャットルームごとに設定でき、1つのタスクに複数付けられます。色で見分けられます。
              </InfoBox>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {tags.map((tg) => (
                  <span
                    key={tg.id}
                    style={{
                      padding: "3px 12px",
                      borderRadius: 9999,
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      border: `1px solid ${tg.color}80`,
                      backgroundColor: `${tg.color}20`,
                      color: tg.color,
                    }}
                  >
                    #{tg.name}
                  </span>
                ))}
              </div>
              <div data-tut="settings-add-tag">
                <p
                  style={{
                    fontSize: "0.8rem",
                    fontWeight: 700,
                    color: text,
                    marginBottom: 6,
                  }}
                >
                  新しいタグを追加
                </p>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    padding: 12,
                    backgroundColor: sectionBg,
                    border: `1px solid ${border}`,
                    borderRadius: 8,
                  }}
                >
                  <input
                    type="text"
                    value={tagName}
                    onChange={(e) => setTagName(e.target.value)}
                    placeholder="新しいタグ名（例: 要確認）"
                    style={{ ...inputStyle, flex: "unset", width: "100%" }}
                  />
                  <TagColorGrid value={tagColor} onPick={setTagColor} />
                  <button
                    type="button"
                    onClick={() => {
                      if (!tagName.trim()) return;
                      onAddTag?.(tagName.trim(), tagColor);
                      setTagName("");
                    }}
                    disabled={!tagName.trim()}
                    style={{
                      padding: "8px 16px",
                      borderRadius: 6,
                      fontWeight: 700,
                      color: "#fff",
                      backgroundColor: tagName.trim() ? "#40C063" : "#cbd5e1",
                      cursor: tagName.trim() ? "pointer" : "not-allowed",
                    }}
                  >
                    タグを追加
                  </button>
                </div>
              </div>
            </>
          )}

          {tab === "custom" && (
            <>
              <InfoBox title="カスタム項目とは">
                プロジェクトごとに独自の入力欄を追加できる機能です。標準の項目だけでは足りない情報をタスクに持たせられます。
              </InfoBox>
              <div
                style={{ display: "flex", flexDirection: "column", gap: 6 }}
              >
                {customColumns.length === 0 ? (
                  <p style={{ fontSize: "0.75rem", color: subText }}>
                    カスタム項目がまだありません。
                  </p>
                ) : (
                  customColumns.map((col) => (
                    <div
                      key={col.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "8px 10px",
                        backgroundColor: sectionBg,
                        border: `1px solid ${border}`,
                        borderRadius: 6,
                      }}
                    >
                      <span style={{ fontWeight: 600, color: text, fontSize: "0.85rem" }}>
                        {col.name}
                      </span>
                      <span
                        style={{
                          fontSize: "0.7rem",
                          color: subText,
                          backgroundColor: inputBg,
                          border: `1px solid ${border}`,
                          padding: "1px 6px",
                          borderRadius: 4,
                        }}
                      >
                        {col.type === "text" ? "テキスト" : "選択肢"}
                      </span>
                      {col.type === "select" && (col.options?.length ?? 0) > 0 && (
                        <span
                          style={{
                            fontSize: "0.7rem",
                            color: subText,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          ({col.options?.join(", ")})
                        </span>
                      )}
                    </div>
                  ))
                )}
              </div>
              <div data-tut="settings-add-custom">
                <p
                  style={{
                    fontSize: "0.8rem",
                    fontWeight: 700,
                    color: text,
                    marginBottom: 6,
                  }}
                >
                  カスタム項目を追加
                </p>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    padding: 12,
                    backgroundColor: sectionBg,
                    border: `1px solid ${border}`,
                    borderRadius: 8,
                  }}
                >
                  <input
                    type="text"
                    value={columnName}
                    onChange={(e) => setColumnName(e.target.value)}
                    placeholder="項目名（例: 見積金額）"
                    style={{ ...inputStyle, flex: "unset", width: "100%" }}
                  />

                  {/* 入力形式の選択（テキスト / 選択肢） */}
                  <div style={{ display: "flex", gap: 18 }}>
                    {(["text", "select"] as const).map((t) => (
                      <label
                        key={t}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                          fontSize: "0.8rem",
                          fontWeight: 600,
                          color: text,
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="radio"
                          name="sandbox-column-type"
                          checked={columnType === t}
                          onChange={() => {
                            setColumnType(t);
                            if (t === "select" && trimmedOptions.length === 0) {
                              setColumnOptions([""]);
                            }
                          }}
                          style={{ accentColor: accent }}
                        />
                        {t === "text" ? "テキスト（自由入力）" : "選択肢（候補から選ぶ）"}
                      </label>
                    ))}
                  </div>

                  {/* 選択肢の入力欄（選択肢型のときのみ） */}
                  {columnType === "select" && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                      }}
                    >
                      {columnOptions.map((opt, i) => (
                        <div
                          key={i}
                          style={{ display: "flex", alignItems: "center", gap: 6 }}
                        >
                          <input
                            type="text"
                            value={opt}
                            onChange={(e) => {
                              const opts = [...columnOptions];
                              opts[i] = e.target.value;
                              setColumnOptions(opts);
                            }}
                            placeholder={`選択肢 ${i + 1}`}
                            style={inputStyle}
                          />
                          {columnOptions.length > 1 && (
                            <button
                              type="button"
                              onClick={() =>
                                setColumnOptions((opts) =>
                                  opts.filter((_, j) => j !== i),
                                )
                              }
                              aria-label={`選択肢 ${i + 1} を削除`}
                              style={{
                                flexShrink: 0,
                                color: "#F85B52",
                                padding: "0 4px",
                                fontSize: "0.9rem",
                              }}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() =>
                          setColumnOptions((opts) => [...opts, ""])
                        }
                        style={{
                          alignSelf: "flex-start",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          color: accent,
                        }}
                      >
                        ＋ 選択肢を追加
                      </button>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => {
                      if (!canAddColumn) return;
                      onAddCustomColumn?.(
                        columnName.trim(),
                        columnType,
                        columnType === "select" ? trimmedOptions : [],
                      );
                      resetColumnForm();
                    }}
                    disabled={!canAddColumn}
                    style={{
                      alignSelf: "flex-end",
                      padding: "8px 20px",
                      borderRadius: 6,
                      fontWeight: 700,
                      color: "#fff",
                      backgroundColor: canAddColumn ? accent : "#cbd5e1",
                      cursor: canAddColumn ? "pointer" : "not-allowed",
                    }}
                  >
                    追加
                  </button>
                </div>
              </div>
            </>
          )}

          {tab === "autoReminder" && autoReminderForm && (
            <AutoReminderTab
              projectName={projectName ?? "プロジェクト"}
              form={autoReminderForm}
              saved={!!autoReminderSaved}
              statuses={statuses}
              tags={tags}
              accent={accent}
              text={text}
              subText={subText}
              border={border}
              inputBg={inputBg}
              sectionBg={sectionBg}
              infoBg={infoBg}
              infoBorder={infoBorder}
              onToggle={onToggleAutoReminder}
              onUpdate={onUpdateAutoReminderForm}
              onSave={onSaveAutoReminder}
            />
          )}
        </div>

        {/* フッター */}
        <div
          style={{
            padding: 14,
            borderTop: `1px solid ${border}`,
            backgroundColor: sectionBg,
            display: "flex",
            justifyContent: "flex-end",
            flexShrink: 0,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "8px 20px",
              borderRadius: 8,
              fontWeight: 700,
              color: text,
              border: `1px solid ${border}`,
              backgroundColor: surface,
            }}
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
};

// ===== 自動リマインダータブ（実画面 GeneralSettingsDialog の自動リマインダーを簡略再現） =====

interface AutoReminderTabProps {
  projectName: string;
  form: SandboxAutoReminderForm;
  saved: boolean;
  statuses: SandboxStatusDef[];
  tags: SandboxTag[];
  accent: string;
  text: string;
  subText: string;
  border: string;
  inputBg: string;
  sectionBg: string;
  infoBg: string;
  infoBorder: string;
  onToggle?: () => void;
  onUpdate?: (updates: Partial<SandboxAutoReminderForm>) => void;
  onSave?: () => void;
}

const AUTO_REMINDER_PRIORITIES: SandboxPriority[] = ["高", "中", "低", "なし"];
const AUTO_REMINDER_ALWAYS_EXCLUDED = ["完了", "非表示"];

const AutoReminderTab = ({
  projectName,
  form,
  saved,
  statuses,
  tags,
  accent,
  text,
  subText,
  border,
  inputBg,
  sectionBg,
  infoBg,
  infoBorder,
  onToggle,
  onUpdate,
  onSave,
}: AutoReminderTabProps) => {
  const [tagPickerOpen, setTagPickerOpen] = useState(false);
  const selectableStatuses = statuses
    .map((s) => s.name)
    .filter((name) => !AUTO_REMINDER_ALWAYS_EXCLUDED.includes(name));

  const togglePriority = (p: SandboxPriority) => {
    const next = form.priorities.includes(p)
      ? form.priorities.filter((x) => x !== p)
      : [...form.priorities, p];
    onUpdate?.({ priorities: next });
  };
  const toggleStatus = (s: string) => {
    const next = form.statuses.includes(s)
      ? form.statuses.filter((x) => x !== s)
      : [...form.statuses, s];
    onUpdate?.({ statuses: next });
  };
  const toggleTag = (tagId: number) => {
    const next = form.tags.includes(tagId)
      ? form.tags.filter((x) => x !== tagId)
      : [...form.tags, tagId];
    onUpdate?.({ tags: next });
  };

  const checkboxLabel: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: "0.85rem",
    color: text,
    cursor: "pointer",
  };

  // 「実行タイミング」「対象タスクの条件」のグループ枠（実画面と同じ見た目）
  const groupBlock: React.CSSProperties = {
    border: `1px solid ${border}`,
    borderRadius: 8,
    overflow: "hidden",
  };
  const groupHeader: React.CSSProperties = {
    backgroundColor: sectionBg,
    padding: "6px 12px",
    borderBottom: `1px solid ${border}`,
    fontSize: "0.75rem",
    fontWeight: 700,
    color: subText,
  };

  return (
    <>
      <div
        data-tut="auto-reminder-info"
        style={{
          backgroundColor: infoBg,
          border: `1px solid ${infoBorder}`,
          borderRadius: 8,
          padding: 14,
          fontSize: "0.8rem",
          lineHeight: 1.7,
          color: text,
        }}
      >
        <p style={{ fontWeight: 700, marginBottom: 4 }}>自動リマインダーとは</p>
        <p>
          プロジェクトごとに、期限のあるタスクへリマインダーを自動で設定する機能です。
          1つのプロジェクトに複数のルールを作れ、各ルールで対象の優先度・ステータス・タグと「期限の何日前」を決めておくと、条件に合うタスクへ自動でリマインダーが付きます。
        </p>
        <p style={{ marginTop: 8 }}>
          条件が合うルールが複数あるタスクには、ルールごとにリマインダーが付きます（例: タグAは2日前・タグBは1週間前）。
        </p>
        <p style={{ marginTop: 8 }}>
          タスクの期限・優先度・ステータス・タグを変更すると、リマインダーも自動で付け直されます。完了・非表示のタスクには通知されません。
        </p>
      </div>

      <div
        style={{
          border: `1px solid ${border}`,
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            backgroundColor: sectionBg,
            padding: "8px 14px",
            borderBottom: `1px solid ${border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span style={{ fontWeight: 700, fontSize: "0.85rem", color: text }}>
            {projectName}
          </span>
          {!form.isEnabled && (
            <span
              style={{
                fontSize: "0.7rem",
                color: subText,
                backgroundColor: inputBg,
                padding: "2px 8px",
                borderRadius: 4,
                border: `1px solid ${border}`,
              }}
            >
              自動リマインダーOFF（未設定）
            </span>
          )}
        </div>

        <div
          style={{
            padding: 16,
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          {/* ON/OFF トグル */}
          <label
            data-tut="auto-reminder-toggle"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              cursor: "pointer",
            }}
            onClick={onToggle}
          >
            <div
              style={{
                position: "relative",
                width: 44,
                height: 24,
                borderRadius: 9999,
                flexShrink: 0,
                backgroundColor: form.isEnabled ? accent : "#cbd5e1",
                transition: "background-color 0.15s",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 2,
                  left: form.isEnabled ? 22 : 2,
                  width: 20,
                  height: 20,
                  borderRadius: 9999,
                  backgroundColor: "#fff",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
                  transition: "left 0.15s",
                }}
              />
            </div>
            <span style={{ fontSize: "0.85rem", fontWeight: 600, color: text }}>
              自動リマインダー {form.isEnabled ? "ON" : "OFF"}
            </span>
          </label>

          {form.isEnabled && (
            <div
              data-tut="auto-reminder-form"
              style={{ display: "flex", flexDirection: "column", gap: 16 }}
            >
              {/* ルール名（任意） */}
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span
                  style={{ fontSize: "0.75rem", fontWeight: 600, color: subText }}
                >
                  ルール名（任意）
                </span>
                <input
                  type="text"
                  value={form.ruleName}
                  placeholder="例：重要タスクの前日通知"
                  onChange={(e) => onUpdate?.({ ruleName: e.target.value })}
                  style={{
                    padding: "6px 8px",
                    border: `1px solid ${border}`,
                    borderRadius: 6,
                    backgroundColor: inputBg,
                    color: text,
                    fontSize: "0.85rem",
                  }}
                />
              </div>

              {/* ステップ1: 実行タイミング */}
              <div style={groupBlock}>
                <div style={groupHeader}>実行タイミング</div>
                <div
                  style={{
                    padding: 12,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                    fontSize: "0.85rem",
                    color: text,
                  }}
                >
                  <span>期限の</span>
                  <input
                    type="number"
                    min={0}
                    max={30}
                    value={form.daysBefore}
                    onChange={(e) =>
                      onUpdate?.({
                        daysBefore: Math.max(
                          0,
                          Math.min(30, Number(e.target.value)),
                        ),
                      })
                    }
                    style={{
                      width: 56,
                      padding: "6px 8px",
                      textAlign: "center",
                      border: `1px solid ${border}`,
                      borderRadius: 6,
                      backgroundColor: inputBg,
                      color: text,
                      fontSize: "0.85rem",
                    }}
                  />
                  <span>日前の</span>
                  <select
                    value={form.hour}
                    onChange={(e) => onUpdate?.({ hour: Number(e.target.value) })}
                    style={{
                      padding: "6px 8px",
                      border: `1px solid ${border}`,
                      borderRadius: 6,
                      backgroundColor: inputBg,
                      color: text,
                      fontSize: "0.85rem",
                    }}
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>
                        {i}時
                      </option>
                    ))}
                  </select>
                  <span>にリマインドする</span>
                </div>
              </div>

              {/* ステップ2: 対象タスクの条件 */}
              <div style={groupBlock}>
                <div style={groupHeader}>対象タスクの条件</div>
                <div
                  style={{
                    padding: 12,
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                  }}
                >
                  <span style={{ fontSize: "0.75rem", color: subText }}>
                    以下の条件を
                    <span style={{ fontWeight: 700 }}>すべて満たす</span>
                    タスクが対象になります。
                  </span>

                  {/* 優先度 */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <span
                      style={{ fontSize: "0.75rem", fontWeight: 600, color: subText }}
                    >
                      優先度
                    </span>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                      {AUTO_REMINDER_PRIORITIES.map((p) => (
                        <label key={p} style={checkboxLabel}>
                          <input
                            type="checkbox"
                            checked={form.priorities.includes(p)}
                            onChange={() => togglePriority(p)}
                          />
                          {p}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* ステータス */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <span
                      style={{ fontSize: "0.75rem", fontWeight: 600, color: subText }}
                    >
                      ステータス
                    </span>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                      {selectableStatuses.map((s) => (
                        <label key={s} style={checkboxLabel}>
                          <input
                            type="checkbox"
                            checked={form.statuses.includes(s)}
                            onChange={() => toggleStatus(s)}
                          />
                          {s}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* タグ */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <span
                      style={{ fontSize: "0.75rem", fontWeight: 600, color: subText }}
                    >
                      タグ
                    </span>
                    {tags.length === 0 ? (
                      <span style={{ fontSize: "0.8rem", color: subText }}>
                        タグがありません。
                      </span>
                    ) : (
                      <>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          {form.tags.map((tagId) => {
                            const tag = tags.find((t) => t.id === tagId);
                            if (!tag) return null;
                            return (
                              <span
                                key={tag.id}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                  padding: "2px 4px 2px 10px",
                                  borderRadius: 9999,
                                  fontSize: "0.75rem",
                                  backgroundColor: tag.color,
                                  color: "#fff",
                                }}
                              >
                                {tag.name}
                                <button
                                  type="button"
                                  onClick={() => toggleTag(tag.id)}
                                  aria-label={`タグ「${tag.name}」を外す`}
                                  style={{
                                    width: 16,
                                    height: 16,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: 9999,
                                    color: "#fff",
                                    cursor: "pointer",
                                    lineHeight: 1,
                                  }}
                                >
                                  ✕
                                </button>
                              </span>
                            );
                          })}
                          {form.tags.length === 0 && (
                            <span style={{ fontSize: "0.75rem", color: subText }}>
                              未指定（すべてのタスクが対象）
                            </span>
                          )}
                          {tags.some((t) => !form.tags.includes(t.id)) && (
                            <button
                              type="button"
                              onClick={() => setTagPickerOpen((o) => !o)}
                              style={{
                                padding: "2px 10px",
                                borderRadius: 9999,
                                fontSize: "0.75rem",
                                border: `1px dashed ${subText}`,
                                backgroundColor: inputBg,
                                color: text,
                                cursor: "pointer",
                              }}
                            >
                              ＋ タグを追加
                            </button>
                          )}
                        </div>
                        {tagPickerOpen &&
                          tags.some((t) => !form.tags.includes(t.id)) && (
                            <div
                              style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 8,
                                padding: 8,
                                border: `1px solid ${border}`,
                                borderRadius: 6,
                                backgroundColor: sectionBg,
                              }}
                            >
                              {tags
                                .filter((t) => !form.tags.includes(t.id))
                                .map((tag) => (
                                  <button
                                    key={tag.id}
                                    type="button"
                                    onClick={() => toggleTag(tag.id)}
                                    style={{
                                      padding: "2px 10px",
                                      borderRadius: 9999,
                                      fontSize: "0.75rem",
                                      border: `1px solid ${tag.color}`,
                                      backgroundColor: inputBg,
                                      color: text,
                                      cursor: "pointer",
                                    }}
                                  >
                                    ＋ {tag.name}
                                  </button>
                                ))}
                            </div>
                          )}
                        {form.tags.length >= 2 && (
                          <span style={{ fontSize: "0.75rem", color: "#d97706" }}>
                            ※ 選択した{form.tags.length}個のタグを
                            <span style={{ fontWeight: 700 }}>すべて</span>
                            持つタスクだけが対象です（「いずれか1つ」ではありません）
                          </span>
                        )}
                      </>
                    )}
                  </div>

                  <span style={{ fontSize: "0.75rem", color: subText }}>
                    ※ 完了・非表示ステータスのタスクにはリマインドされません。
                  </span>
                </div>
              </div>

              {/* 既存タスクにも適用 */}
              <label style={checkboxLabel}>
                <input
                  type="checkbox"
                  checked={form.applyToExisting}
                  onChange={(e) =>
                    onUpdate?.({ applyToExisting: e.target.checked })
                  }
                />
                保存時に既存タスクにも適用する
              </label>
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              type="button"
              data-tut="auto-reminder-save"
              onClick={onSave}
              style={{
                padding: "8px 20px",
                borderRadius: 6,
                fontWeight: 700,
                fontSize: "0.85rem",
                color: "#fff",
                backgroundColor: accent,
                cursor: onSave ? "pointer" : "default",
              }}
            >
              保存
            </button>
            {saved && (
              <span style={{ fontSize: "0.8rem", color: "#40C063" }}>
                保存しました
              </span>
            )}
          </div>

          {/* 複数ルールの案内（このチュートリアルでは 1 ルールのみ操作） */}
          <span style={{ fontSize: "0.75rem", color: subText }}>
            実際のアプリでは「＋ ルールを追加」から、1つのプロジェクトに複数のルールを設定できます。
          </span>
        </div>
      </div>
    </>
  );
};

// ===== タスク作成: 担当者/依頼者ピッカー =====

interface MemberPickerProps {
  label: string;
  icon: React.ReactNode;
  selected: string[];
  onToggle: (name: string) => void;
  accent: string;
  text: string;
  subText: string;
  border: string;
  inputBg: string;
  chipBg: string;
}

const MemberPicker = ({
  label,
  icon,
  selected,
  onToggle,
  accent,
  text,
  subText,
  border,
  inputBg,
  chipBg,
}: MemberPickerProps) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <SectionLabel
        icon={icon}
        label={`${label} (${selected.length}名)`}
        subText={subText}
      />
      <div
        onClick={() => setOpen((o) => !o)}
        style={{
          marginTop: 4,
          minHeight: 40,
          padding: 6,
          border: `1px solid ${border}`,
          borderRadius: 6,
          backgroundColor: inputBg,
          cursor: "pointer",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 4,
        }}
      >
        {selected.map((name) => (
          <span
            key={name}
            style={{
              fontSize: "0.75rem",
              padding: "2px 8px",
              backgroundColor: chipBg,
              borderRadius: 9999,
              color: text,
            }}
          >
            {name}
          </span>
        ))}
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            marginLeft: "auto",
            fontSize: "0.75rem",
            color: accent,
          }}
        >
          <PersonAddTwoToneIcon style={{ fontSize: 14 }} />
          追加
        </span>
      </div>
      {open && (
        <div
          style={{
            marginTop: 4,
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
          }}
        >
          {SANDBOX_MEMBERS.map((name) => {
            const isSel = selected.includes(name);
            return (
              <button
                key={name}
                type="button"
                onClick={() => onToggle(name)}
                style={{
                  fontSize: "0.75rem",
                  padding: "3px 10px",
                  borderRadius: 9999,
                  border: `1px solid ${isSel ? accent : border}`,
                  backgroundColor: isSel ? `${accent}20` : "transparent",
                  color: isSel ? accent : subText,
                  fontWeight: 600,
                }}
              >
                {name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ===== タスク作成: 期限フィールド =====

interface DateFieldProps {
  startDate?: string;
  endDate?: string;
  onChange: (field: "startDate" | "endDate", v: string) => void;
  accent: string;
  text: string;
  subText: string;
  border: string;
  inputBg: string;
  isDark: boolean;
}

const DateField = ({
  startDate,
  endDate,
  onChange,
  accent,
  text,
  subText,
  border,
  inputBg,
  isDark,
}: DateFieldProps) => {
  const [range, setRange] = useState(false);
  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: 8,
    border: `1px solid ${border}`,
    borderRadius: 6,
    outline: "none",
    backgroundColor: inputBg,
    color: text,
    fontSize: "0.85rem",
  };
  return (
    <div>
      <SectionLabel
        icon={<EventTwoToneIcon style={{ fontSize: 16, color: subText }} />}
        label="期限"
        subText={subText}
      />
      <div
        style={{
          marginTop: 4,
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <input
          type="date"
          value={startDate ?? ""}
          onChange={(e) => onChange("startDate", e.target.value)}
          style={inputStyle}
        />
        {range && (
          <>
            <span style={{ color: subText }}>～</span>
            <input
              type="date"
              value={endDate ?? ""}
              onChange={(e) => onChange("endDate", e.target.value)}
              style={inputStyle}
            />
          </>
        )}
      </div>
      <label
        onClick={() => {
          const next = !range;
          setRange(next);
          if (!next) onChange("endDate", "");
        }}
        style={{
          marginTop: 6,
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: "0.75rem",
          color: subText,
          cursor: "pointer",
        }}
      >
        <span
          style={{
            width: 32,
            height: 18,
            borderRadius: 9999,
            backgroundColor: range ? accent : isDark ? "#555" : "#cbd5e1",
            position: "relative",
            flexShrink: 0,
            transition: "background-color 0.15s",
          }}
        >
          <span
            style={{
              position: "absolute",
              top: 2,
              left: range ? 16 : 2,
              width: 14,
              height: 14,
              borderRadius: 9999,
              backgroundColor: "#fff",
              transition: "left 0.15s",
            }}
          />
        </span>
        期間を設ける
      </label>
    </div>
  );
};

// ===== タスク作成: タグピッカー =====

const TagPicker = ({
  tags,
  selected,
  onToggle,
  accent,
  subText,
  inputBg,
}: {
  tags: SandboxTag[];
  selected: string[];
  onToggle: (name: string) => void;
  accent: string;
  subText: string;
  inputBg: string;
}) => (
  <div>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <SectionLabel
        icon={<StyleTwoToneIcon style={{ fontSize: 16, color: subText }} />}
        label="タグ"
        subText={subText}
      />
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          fontSize: "0.75rem",
          color: accent,
        }}
      >
        <NewLabelTwoToneIcon style={{ fontSize: 14 }} />
        追加・編集
      </span>
    </div>
    <div
      style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 8 }}
    >
      {tags.map((tag) => {
        const isSel = selected.includes(tag.name);
        return (
          <button
            key={tag.id}
            type="button"
            onClick={() => onToggle(tag.name)}
            style={{
              padding: "3px 12px",
              borderRadius: 9999,
              fontSize: "0.75rem",
              fontWeight: 600,
              border: `1px solid ${tag.color}80`,
              backgroundColor: isSel ? `${tag.color}20` : inputBg,
              color: isSel ? tag.color : `${tag.color}B0`,
            }}
          >
            #{tag.name}
          </button>
        );
      })}
    </div>
  </div>
);

// ===== タスク作成: カスタム項目フィールド =====

const CustomColumnFields = ({
  columns,
  values,
  onChange,
  text,
  subText,
  border,
  inputBg,
}: {
  columns: SandboxCustomColumn[];
  values: Record<number, string>;
  onChange: (columnId: number, v: string) => void;
  text: string;
  subText: string;
  border: string;
  inputBg: string;
}) => {
  const fieldStyle: React.CSSProperties = {
    flex: 1,
    padding: "5px 8px",
    border: `1px solid ${border}`,
    borderRadius: 6,
    outline: "none",
    backgroundColor: inputBg,
    color: text,
    fontSize: "0.8rem",
  };
  return (
    <div>
      <SectionLabel
        icon={<ExtensionTwoToneIcon style={{ fontSize: 16, color: subText }} />}
        label="カスタム項目"
        subText={subText}
      />
      <div
        style={{
          marginTop: 6,
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        {columns.map((col) => (
          <div
            key={col.id}
            style={{ display: "flex", alignItems: "center", gap: 8 }}
          >
            <span
              style={{
                width: 88,
                flexShrink: 0,
                fontSize: "0.75rem",
                color: subText,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {col.name}
            </span>
            {col.type === "select" ? (
              <select
                value={values[col.id] ?? ""}
                onChange={(e) => onChange(col.id, e.target.value)}
                style={fieldStyle}
              >
                <option value="">選択してください</option>
                {(col.options ?? []).map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={values[col.id] ?? ""}
                onChange={(e) => onChange(col.id, e.target.value)}
                placeholder="入力してください"
                style={fieldStyle}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ===== 浮遊ツールバー: アイコンボタン / 区切り線 =====

const ToolbarIconButton = ({
  children,
  subText,
  active = false,
  activeStyle,
}: {
  children: React.ReactNode;
  subText: string;
  active?: boolean;
  activeStyle?: React.CSSProperties;
}) => (
  <span
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 6,
      margin: "0 1px",
      borderRadius: 6,
      color: active ? "#fff" : subText,
      ...(active ? activeStyle : {}),
    }}
  >
    {children}
  </span>
);

const ToolbarDivider = ({ border }: { border: string }) => (
  <span
    style={{
      width: 1,
      alignSelf: "stretch",
      margin: "0 4px",
      backgroundColor: border,
    }}
  />
);

// ===== サイドバー: セクション見出し =====

const SidebarSectionTitle = ({
  children,
  subText,
}: {
  children: React.ReactNode;
  subText: string;
}) => (
  <span
    style={{
      fontSize: "0.8rem",
      fontWeight: 700,
      color: subText,
      display: "block",
      marginBottom: 8,
    }}
  >
    {children}
  </span>
);

// ===== サイドバー: フィルタートグル =====

const FilterToggle = ({
  label,
  checked,
  labelColor,
}: {
  label: string;
  checked: boolean;
  labelColor: string;
}) => (
  <label
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      fontSize: "0.85rem",
      cursor: "pointer",
      paddingLeft: 8,
      color: "inherit",
    }}
  >
    <span style={{ color: labelColor }}>{label}</span>
    <span
      style={{
        width: 44,
        height: 24,
        borderRadius: 9999,
        backgroundColor: checked ? "#22c55e" : "#d1d5db",
        position: "relative",
        transition: "background-color 0.15s",
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 2,
          left: checked ? 22 : 2,
          width: 20,
          height: 20,
          borderRadius: 9999,
          backgroundColor: "#fff",
          boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
          transition: "left 0.15s",
        }}
      />
    </span>
  </label>
);

// ===== タスク行 =====

interface TaskRowProps {
  task: SandboxTask;
  isSelected: boolean;
  statusMenuOpen: boolean;
  statuses: SandboxStatusDef[];
  accent: string;
  rowBg: string;
  chipBg: string;
  border: string;
  text: string;
  subText: string;
  /** タスクカードに表示するカスタム項目（表示順） */
  customItems: { id: number; name: string; value: string }[];
  /** ハイライト用の data-tut 値（未指定なら "task-row"） */
  dataTut?: string;
  /** 繰り返しアイコンを表示する（自動作成タスク用） */
  showRecurringIcon?: boolean;
  onSelect: () => void;
  onToggleStatusMenu: () => void;
  onChangeStatus: (s: SandboxStatus) => void;
  onToggleComplete: () => void;
}

const TaskRow = ({
  task,
  isSelected,
  statusMenuOpen,
  statuses,
  accent,
  rowBg,
  chipBg,
  border,
  text,
  subText,
  customItems,
  dataTut = "task-row",
  showRecurringIcon = false,
  onSelect,
  onToggleStatusMenu,
  onChangeStatus,
  onToggleComplete,
}: TaskRowProps) => {
  // 1行に最大2項目ずつ表示するためペアに分割する
  const customItemRows: { id: number; name: string; value: string }[][] = [];
  for (let i = 0; i < customItems.length; i += 2) {
    customItemRows.push(customItems.slice(i, i + 2));
  }
  const badge = PRIORITY_BADGE[task.priority];
  const statusColor = resolveStatusColor(task.status, statuses);
  const titleColor = task.completed
    ? subText
    : task.status === "未対応"
      ? text
      : statusColor;

  return (
    <div
      data-tut={dataTut}
      onClick={onSelect}
      style={{
        backgroundColor: isSelected ? `${accent}14` : rowBg,
        border: `1px solid ${isSelected ? accent : border}`,
        borderRadius: 10,
        padding: 12,
        cursor: "pointer",
        boxShadow: isSelected ? "0 4px 10px rgba(0,0,0,0.12)" : "none",
      }}
    >
      {/* 上段: 優先度バッジ + 繰り返しアイコン + 通知/スター（装飾） */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: 24,
          marginBottom: badge.label || showRecurringIcon ? 2 : 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {badge.label && (
            <span
              style={{
                display: "inline-block",
                backgroundColor: badge.bg,
                color: "#fff",
                fontSize: "0.625rem",
                fontWeight: 600,
                lineHeight: 1,
                padding: "2px 6px",
                borderRadius: 9999,
              }}
            >
              {badge.label}
            </span>
          )}
          {showRecurringIcon && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 2,
                color: accent,
                fontSize: "0.625rem",
                fontWeight: 700,
              }}
            >
              <AutorenewIcon style={{ fontSize: 14 }} />
              自動作成
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
          <NotificationsOffIcon style={{ fontSize: 18, color: "#d1d5db" }} />
          <StarBorderIcon style={{ fontSize: 20, color: "#d1d5db" }} />
        </div>
      </div>

      {/* 中段: チェックボックス + ステータス + タイトル */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button
          type="button"
          data-tut="task-checkbox"
          aria-label={task.completed ? "未完了に戻す" : "タスクを完了にする"}
          onClick={(e) => {
            e.stopPropagation();
            onToggleComplete();
          }}
          style={{
            width: 20,
            height: 20,
            borderRadius: 4,
            border: `2px solid ${task.completed ? "#22c55e" : "#9ca3af"}`,
            backgroundColor: task.completed ? "#22c55e" : "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            cursor: "pointer",
            padding: 0,
          }}
        >
          {task.completed && (
            <CheckIcon style={{ fontSize: 14, color: "#fff" }} />
          )}
        </button>

        {/* ステータス（インライン展開メニュー） */}
        <div
          data-tut="task-status"
          style={{ position: "relative", flexShrink: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={onToggleStatusMenu}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              height: 24,
              backgroundColor: chipBg,
              padding: "0 8px",
              borderRadius: 6,
              fontSize: "0.75rem",
              fontWeight: 600,
              color: statusColor,
              whiteSpace: "nowrap",
            }}
          >
            {task.status}
            <ExpandMoreIcon
              style={{
                fontSize: 16,
                color: subText,
                transform: statusMenuOpen ? "rotate(180deg)" : "none",
                transition: "transform 0.15s",
              }}
            />
          </button>
          {statusMenuOpen && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                marginTop: 4,
                backgroundColor: rowBg,
                border: `1px solid ${border}`,
                borderRadius: 8,
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                overflow: "hidden",
                minWidth: 120,
                zIndex: 30,
              }}
            >
              {statuses.map((s) => (
                <button
                  key={s.name}
                  type="button"
                  onClick={() => onChangeStatus(s.name)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    width: "100%",
                    padding: "6px 12px",
                    fontSize: "0.8rem",
                    color: text,
                    textAlign: "left",
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 9999,
                      backgroundColor: s.color,
                    }}
                  />
                  {s.name}
                </button>
              ))}
            </div>
          )}
        </div>

        <h3
          style={{
            flex: 1,
            minWidth: 0,
            fontWeight: 600,
            color: titleColor,
            textDecoration: task.completed ? "line-through" : "none",
            lineHeight: 1.4,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {task.title}
        </h3>
      </div>

      {/* 下段: 担当/期限 もしくは 完了者/完了日 */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          marginTop: 6,
          marginLeft: 30,
          fontSize: "0.75rem",
          color: subText,
        }}
      >
        {task.completed ? (
          <>
            <span>
              <strong style={{ color: subText }}>完了者:</strong>{" "}
              {task.completedBy ?? "ー"}
            </span>
            <span>
              <strong style={{ color: subText }}>完了日:</strong>{" "}
              {formatYmd(task.completedAt)}
            </span>
          </>
        ) : (
          <>
            <span>
              <strong style={{ color: subText }}>担当:</strong>{" "}
              {task.assignees.length > 0 ? task.assignees.join(", ") : "ー"}
            </span>
            <span>
              <strong style={{ color: subText }}>期限:</strong>{" "}
              {task.startDate
                ? task.endDate && task.endDate !== task.startDate
                  ? `${formatYmd(task.startDate)} ～ ${formatYmd(task.endDate)}`
                  : formatYmd(task.startDate)
                : "ー"}
            </span>
          </>
        )}
      </div>

      {/* カスタム項目（「表示するカスタム項目」で選択された列のみ。1行に2項目） */}
      {!task.completed && customItemRows.length > 0 && (
        <div
          style={{
            marginTop: 6,
            marginLeft: 30,
            display: "flex",
            flexDirection: "column",
            gap: 4,
            fontSize: "0.75rem",
            color: subText,
          }}
        >
          {customItemRows.map((row, rowIndex) => (
            <div
              key={rowIndex}
              style={{ display: "flex", alignItems: "center", gap: 16 }}
            >
              {row.map((item) => (
                <div
                  key={item.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    minWidth: 0,
                  }}
                >
                  <strong
                    style={{
                      color: subText,
                      flexShrink: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "8rem",
                    }}
                  >
                    {item.name}:
                  </strong>
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.value || "ー"}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ===== 詳細パネル =====

interface DetailPanelProps {
  task: SandboxTask;
  accent: string;
  isDark: boolean;
  text: string;
  subText: string;
  border: string;
  inputBg: string;
  chipBg: string;
  rowBg: string;
  statuses: SandboxStatusDef[];
  tags: SandboxTag[];
  customColumns: SandboxCustomColumn[];
  onPriorityChange: (p: SandboxPriority) => void;
  onMemoChange: (v: string) => void;
  /** 詳細パネルでステータスメニューを開いたとき（設定チュートリアル用） */
  onOpenStatusMenu?: () => void;
  /** 詳細パネルでステータスを変更（設定チュートリアル用） */
  onChangeStatus?: (s: SandboxStatus) => void;
  /** 詳細パネルでタグをトグル（設定チュートリアル用） */
  onToggleTag?: (name: string) => void;
  /** 詳細パネルでカスタム項目の値を変更（指定時は編集可能フィールドを表示） */
  onCustomChange?: (columnId: number, v: string) => void;
  // ===== 繰り返し作成チュートリアル用（任意） =====
  /** フッターの「繰り返し」ボタン（指定時のみクリック可） */
  onOpenRecurrence?: () => void;
  /** このタスクが自動作成タスクなら true（自動作成バッジを表示） */
  autoCreated?: boolean;
  /** 自動作成の元ルールの要約（頻度など） */
  ruleSummary?: string;
  /** 自動作成バッジの「ルール編集」ボタン */
  onEditRule?: () => void;
  /** フッターの「リマインダー」ボタン（指定時のみクリック可） */
  onOpenReminder?: () => void;
}

const DetailPanel = ({
  task,
  accent,
  isDark,
  text,
  subText,
  border,
  inputBg,
  chipBg,
  rowBg,
  statuses,
  tags,
  customColumns,
  onPriorityChange,
  onMemoChange,
  onOpenStatusMenu,
  onChangeStatus,
  onToggleTag,
  onCustomChange,
  onOpenRecurrence,
  autoCreated = false,
  ruleSummary,
  onEditRule,
  onOpenReminder,
}: DetailPanelProps) => {
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const statusColor = resolveStatusColor(task.status, statuses);
  const footerBtn: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 3,
    padding: "4px 6px",
    borderRadius: 6,
    fontSize: "0.68rem",
    // 幅が狭くてもラベルが折り返して2行にならないようにする
    whiteSpace: "nowrap",
    flexShrink: 0,
    color: subText,
  };
  const iconBtn: React.CSSProperties = {
    width: 30,
    height: 30,
    borderRadius: 8,
    border: `1px solid ${border}`,
    backgroundColor: inputBg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const hasDates = !!task.startDate;
  const dateText = hasDates
    ? task.endDate && task.endDate !== task.startDate
      ? `${formatYmd(task.startDate)} ～ ${formatYmd(task.endDate)}`
      : formatYmd(task.startDate)
    : "未設定";

  return (
    <div
      data-tut="detail-panel"
      style={{ display: "flex", flexDirection: "column", height: "100%" }}
    >
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: 24,
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {/* タイトル */}
        <input
          type="text"
          readOnly
          value={task.title}
          style={{
            fontSize: "1.25rem",
            fontWeight: 700,
            color: text,
            width: "100%",
            border: "none",
            borderBottom: `2px solid ${border}`,
            outline: "none",
            backgroundColor: "transparent",
            paddingBottom: 4,
          }}
        />

        {/* ステータス（設定チュートリアル時はドロップダウン、それ以外はピル表示） */}
        {onChangeStatus ? (
          <div data-tut="detail-status" style={{ position: "relative" }}>
            <button
              type="button"
              onClick={() => {
                const willOpen = !statusMenuOpen;
                setStatusMenuOpen(willOpen);
                if (willOpen) onOpenStatusMenu?.();
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                height: 28,
                fontSize: "0.8rem",
                fontWeight: 600,
                color: statusColor,
                padding: "0 10px",
                backgroundColor: chipBg,
                borderRadius: 6,
              }}
            >
              {task.status}
              <ExpandMoreIcon
                style={{
                  fontSize: 16,
                  color: subText,
                  transform: statusMenuOpen ? "rotate(180deg)" : "none",
                  transition: "transform 0.15s",
                }}
              />
            </button>
            {statusMenuOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  marginTop: 4,
                  backgroundColor: rowBg,
                  border: `1px solid ${border}`,
                  borderRadius: 8,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  overflow: "hidden",
                  minWidth: 140,
                  zIndex: 30,
                }}
              >
                {statuses.map((s) => (
                  <button
                    key={s.name}
                    type="button"
                    onClick={() => {
                      onChangeStatus(s.name);
                      setStatusMenuOpen(false);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      width: "100%",
                      padding: "6px 12px",
                      fontSize: "0.8rem",
                      color: text,
                      textAlign: "left",
                    }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 9999,
                        backgroundColor: s.color,
                      }}
                    />
                    {s.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                height: 24,
                fontSize: "0.75rem",
                fontWeight: 600,
                color: statusColor,
                padding: "0 10px",
                backgroundColor: chipBg,
                borderRadius: 6,
              }}
            >
              {task.status}
            </span>
          </div>
        )}

        {/* 完了情報 */}
        {task.completed && (
          <div
            style={{
              padding: 12,
              backgroundColor: isDark ? "#2a2a2a" : "#f9fafb",
              borderRadius: 8,
              border: `1px solid ${border}`,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <PeopleTwoToneIcon style={{ fontSize: 16, color: subText }} />
              <span style={{ fontSize: "0.8rem", fontWeight: 700 }}>完了者:</span>
              <span style={{ fontSize: "0.8rem" }}>{task.completedBy ?? "ー"}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <EventTwoToneIcon style={{ fontSize: 16, color: subText }} />
              <span style={{ fontSize: "0.8rem", fontWeight: 700 }}>完了日:</span>
              <span style={{ fontSize: "0.8rem" }}>
                {formatYmd(task.completedAt)}
              </span>
            </div>
          </div>
        )}

        {/* 自動作成バッジ（繰り返しチュートリアル用） */}
        {autoCreated && (
          <div
            data-tut="auto-created-badge"
            style={{
              padding: 12,
              borderRadius: 8,
              backgroundColor: `${accent}20`,
              border: `1px solid ${accent}80`,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginBottom: 8,
              }}
            >
              <AutorenewIcon style={{ fontSize: 18, color: accent }} />
              <span
                style={{ fontSize: "0.85rem", fontWeight: 700, color: accent }}
              >
                このタスクは自動作成されました
              </span>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <span style={{ fontSize: "0.8rem", color: text }}>
                <span style={{ fontWeight: 600, color: subText }}>繰り返し: </span>
                {ruleSummary ?? "ー"}
              </span>
              <button
                type="button"
                data-tut="auto-rule-edit-btn"
                onClick={onEditRule}
                style={{
                  flexShrink: 0,
                  padding: "6px 12px",
                  borderRadius: 6,
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  color: "#fff",
                  backgroundColor: accent,
                  cursor: onEditRule ? "pointer" : "default",
                }}
              >
                ルール編集
              </button>
            </div>
          </div>
        )}

        {/* 担当者 */}
        <div>
          <SectionLabel
            icon={<PeopleTwoToneIcon style={{ fontSize: 16, color: subText }} />}
            label={`担当者 (${task.assignees.length}名)`}
            subText={subText}
          />
          <div
            style={{
              marginTop: 4,
              minHeight: 40,
              padding: 6,
              border: `1px solid ${border}`,
              borderRadius: 6,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 4,
            }}
          >
            {task.assignees.length === 0 ? (
              <span style={{ fontSize: "0.8rem", color: subText, padding: "0 4px" }}>
                未設定
              </span>
            ) : (
              task.assignees.map((name) => (
                <span
                  key={name}
                  style={{
                    fontSize: "0.75rem",
                    padding: "2px 8px",
                    backgroundColor: chipBg,
                    borderRadius: 9999,
                    color: text,
                  }}
                >
                  {name}
                </span>
              ))
            )}
          </div>
        </div>

        {/* 期限 */}
        <div>
          <SectionLabel
            icon={<EventTwoToneIcon style={{ fontSize: 16, color: subText }} />}
            label="期限"
            subText={subText}
          />
          <div
            style={{
              marginTop: 4,
              padding: 8,
              minHeight: 38,
              border: `1px solid ${border}`,
              borderRadius: 6,
              backgroundColor: hasDates
                ? inputBg
                : isDark
                  ? "#2a2a2a"
                  : "#f9fafb",
              color: hasDates ? text : subText,
              fontSize: "0.85rem",
              display: "flex",
              alignItems: "center",
            }}
          >
            {dateText}
          </div>
        </div>

        {/* 重要度 */}
        <div data-tut="detail-priority">
          <SectionLabel
            icon={
              <LabelImportantTwoToneIcon
                style={{ fontSize: 16, color: subText }}
              />
            }
            label="重要度"
            subText={subText}
          />
          <div style={{ marginTop: 4 }}>
            <PriorityButtons
              value={task.priority}
              text={text}
              subText={subText}
              isDark={isDark}
              onChange={onPriorityChange}
            />
          </div>
        </div>

        {/* タグ */}
        <div data-tut="detail-tags">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <SectionLabel
              icon={<StyleTwoToneIcon style={{ fontSize: 16, color: subText }} />}
              label="タグ"
              subText={subText}
            />
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                fontSize: "0.75rem",
                color: accent,
              }}
            >
              <NewLabelTwoToneIcon style={{ fontSize: 14 }} />
              追加・編集
            </span>
          </div>
          {onToggleTag ? (
            // 設定チュートリアル: 利用可能なタグから選んで付けられる
            <div
              style={{
                marginTop: 8,
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              {tags.map((tag) => {
                const isSel = task.tags.includes(tag.name);
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => onToggleTag(tag.name)}
                    style={{
                      padding: "3px 12px",
                      borderRadius: 9999,
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      border: `1px solid ${tag.color}80`,
                      backgroundColor: isSel ? `${tag.color}20` : "transparent",
                      color: isSel ? tag.color : `${tag.color}B0`,
                    }}
                  >
                    #{tag.name}
                  </button>
                );
              })}
            </div>
          ) : task.tags.length === 0 ? (
            <div style={{ marginTop: 6, fontSize: "0.75rem", color: subText }}>
              タグはまだありません
            </div>
          ) : (
            <div
              style={{
                marginTop: 8,
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              {task.tags.map((name) => {
                const tag = tags.find((t) => t.name === name);
                const c = tag?.color ?? accent;
                return (
                  <span
                    key={name}
                    style={{
                      padding: "3px 12px",
                      borderRadius: 9999,
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      border: `1px solid ${c}80`,
                      backgroundColor: `${c}20`,
                      color: c,
                    }}
                  >
                    #{name}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {/* カスタム項目 */}
        <div data-tut="detail-custom">
          <SectionLabel
            icon={
              <ExtensionTwoToneIcon style={{ fontSize: 16, color: subText }} />
            }
            label="カスタム項目"
            subText={subText}
          />
          <div
            style={{
              marginTop: 6,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            {customColumns.length === 0 ? (
              <span style={{ fontSize: "0.75rem", color: subText }}>
                カスタム項目はまだありません
              </span>
            ) : null}
            {customColumns.map((col) => {
              const fieldStyle: React.CSSProperties = {
                flex: 1,
                minWidth: 0,
                padding: "5px 8px",
                border: `1px solid ${border}`,
                borderRadius: 6,
                outline: "none",
                backgroundColor: inputBg,
                color: text,
                fontSize: "0.8rem",
              };
              return (
                <div
                  key={col.id}
                  style={{ display: "flex", alignItems: "center", gap: 8 }}
                >
                  <span
                    style={{
                      width: 88,
                      flexShrink: 0,
                      fontSize: "0.75rem",
                      color: subText,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {col.name}
                  </span>
                  {onCustomChange ? (
                    // 設定チュートリアル: テキスト型は入力欄、選択肢型はドロップダウンで編集できる
                    col.type === "select" ? (
                      <select
                        value={task.customValues[col.id] ?? ""}
                        onChange={(e) => onCustomChange(col.id, e.target.value)}
                        style={fieldStyle}
                      >
                        <option value="">（未選択）</option>
                        {(col.options ?? []).map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={task.customValues[col.id] ?? ""}
                        onChange={(e) => onCustomChange(col.id, e.target.value)}
                        placeholder={`${col.name}を入力...`}
                        style={fieldStyle}
                      />
                    )
                  ) : (
                    <span style={{ fontSize: "0.8rem", color: text }}>
                      {task.customValues[col.id] || "ー"}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* メモ */}
        <div>
          <SectionLabel
            icon={<NoteAltTwoToneIcon style={{ fontSize: 16, color: subText }} />}
            label="メモ"
            subText={subText}
          />
          <textarea
            value={task.memo}
            placeholder="メモを入力..."
            onChange={(e) => onMemoChange(e.target.value)}
            style={{
              marginTop: 4,
              width: "100%",
              minHeight: 96,
              padding: 8,
              border: `2px solid ${border}`,
              borderRadius: 8,
              outline: "none",
              resize: "vertical",
              backgroundColor: inputBg,
              color: text,
              fontSize: "0.85rem",
            }}
          />
        </div>
      </div>

      {/* フッターアクションバー（装飾） */}
      <div
        style={{
          borderTop: `1px solid ${border}`,
          padding: "6px 6px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "nowrap",
          gap: 4,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 1 }}>
          <button
            type="button"
            data-tut="detail-reminder-btn"
            onClick={onOpenReminder}
            style={{
              ...footerBtn,
              backgroundColor: "transparent",
              cursor: onOpenReminder ? "pointer" : "default",
            }}
          >
            <AccessAlarmIcon style={{ fontSize: 15 }} />
            リマインダー
          </button>
          <button
            type="button"
            data-tut="detail-recurrence-btn"
            onClick={onOpenRecurrence}
            style={{
              ...footerBtn,
              backgroundColor: "transparent",
              cursor: onOpenRecurrence ? "pointer" : "default",
            }}
          >
            <AutorenewIcon style={{ fontSize: 15 }} />
            繰り返し
          </button>
          <span style={footerBtn}>
            <SaveAltIcon style={{ fontSize: 15 }} />
            テンプレート
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
          <span style={{ ...iconBtn, color: subText }}>
            <ContentCopyIcon style={{ fontSize: 16 }} />
          </span>
          <span style={{ ...iconBtn, color: "#F85B52" }}>
            <DeleteOutlinedIcon style={{ fontSize: 16 }} />
          </span>
        </div>
      </div>
    </div>
  );
};

// ===== 共通: セクションラベル =====

const SectionLabel = ({
  icon,
  label,
  subText,
}: {
  icon: React.ReactNode;
  label: string;
  subText: string;
}) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontSize: "0.8rem",
      fontWeight: 700,
      color: subText,
    }}
  >
    {icon}
    {label}
  </div>
);

// ===== 共通: 重要度ボタン =====

interface PriorityButtonsProps {
  value: SandboxPriority;
  text: string;
  subText: string;
  isDark: boolean;
  onChange: (p: SandboxPriority) => void;
}

const PriorityButtons = ({
  value,
  text,
  subText,
  isDark,
  onChange,
}: PriorityButtonsProps) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(4, 1fr)",
      gap: 4,
      backgroundColor: isDark ? "#2a2a2a" : "#e5e7eb",
      borderRadius: 8,
      padding: 4,
    }}
  >
    {PRIORITIES.map((p) => {
      const selected = p === value;
      return (
        <button
          key={p}
          type="button"
          onClick={() => onChange(p)}
          style={{
            padding: "5px 0",
            borderRadius: 6,
            fontSize: "0.8rem",
            fontWeight: 600,
            backgroundColor: selected
              ? isDark
                ? "#1e1e1e"
                : "#fff"
              : "transparent",
            color: selected ? text : subText,
            boxShadow: selected ? "0 1px 2px rgba(0,0,0,0.15)" : "none",
          }}
        >
          {p}
        </button>
      );
    })}
  </div>
);

// ===== 共通: ダイアログ部品 =====

const DialogShell = ({
  children,
  surface,
  border,
  maxWidth = 420,
}: {
  children: React.ReactNode;
  surface: string;
  border: string;
  maxWidth?: number;
}) => (
  <div
    style={{
      position: "absolute",
      inset: 0,
      backgroundColor: "rgba(0,0,0,0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 50,
      padding: 16,
    }}
  >
    <div
      style={{
        width: "100%",
        maxWidth,
        backgroundColor: surface,
        borderRadius: 12,
        border: `2px solid ${border}`,
        boxShadow: "0 12px 40px rgba(0,0,0,0.4)",
        overflow: "hidden",
      }}
    >
      {children}
    </div>
  </div>
);

/** グラデーションヘッダー（プロジェクト作成ダイアログ用） */
const DialogGradientHeader = ({
  accent,
  title,
  onClose,
}: {
  accent: string;
  title: string;
  onClose: () => void;
}) => (
  <div
    style={{
      background: `linear-gradient(135deg, ${accent}, ${accent}CC)`,
      color: "#fff",
      padding: "12px 16px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <LibraryAddTwoToneIcon style={{ fontSize: 18 }} />
      <span style={{ fontWeight: 700, fontSize: "0.9rem" }}>{title}</span>
    </div>
    <button
      type="button"
      onClick={onClose}
      aria-label="閉じる"
      style={{ color: "#fff", display: "flex", padding: 2 }}
    >
      <CloseIcon fontSize="small" />
    </button>
  </div>
);

/** プレーンヘッダー（タスク作成ダイアログ用・実画面の TaskDialog に準拠） */
const DialogPlainHeader = ({
  title,
  text,
  subText,
  border,
  onClose,
}: {
  title: string;
  text: string;
  subText: string;
  border: string;
  onClose: () => void;
}) => (
  <div
    style={{
      padding: "14px 20px",
      borderBottom: `1px solid ${border}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    }}
  >
    <span style={{ fontWeight: 700, fontSize: "1.125rem", color: text }}>
      {title}
    </span>
    <button
      type="button"
      onClick={onClose}
      aria-label="閉じる"
      style={{ color: subText, display: "flex", padding: 2 }}
    >
      <CloseIcon fontSize="small" />
    </button>
  </div>
);

const DialogFooter = ({
  children,
  surface,
  border,
  grayBg = false,
}: {
  children: React.ReactNode;
  surface: string;
  border: string;
  grayBg?: boolean;
}) => (
  <div
    style={{
      display: "flex",
      gap: 12,
      padding: 16,
      borderTop: `1px solid ${border}`,
      backgroundColor: grayBg ? "#f9fafb" : surface,
    }}
  >
    {children}
  </div>
);

const DialogCancelButton = ({
  onClick,
  text,
  border,
}: {
  onClick: () => void;
  text: string;
  border: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      flex: 1,
      padding: "10px 16px",
      border: `1px solid ${border}`,
      borderRadius: 8,
      fontWeight: 700,
      color: text,
      backgroundColor: "transparent",
    }}
  >
    キャンセル
  </button>
);

const DialogConfirmButton = ({
  onClick,
  disabled,
  accent,
  label,
}: {
  onClick: () => void;
  disabled: boolean;
  accent: string;
  label: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    style={{
      flex: 1,
      padding: "10px 16px",
      borderRadius: 8,
      fontWeight: 700,
      color: "#fff",
      backgroundColor: disabled ? "#cbd5e1" : accent,
      boxShadow: disabled ? "none" : "0 2px 4px rgba(0,0,0,0.15)",
      cursor: disabled ? "not-allowed" : "pointer",
    }}
  >
    {label}
  </button>
);
